// mobile_v1: responsive — ใช้ breakpoint < 1024 รวม mobile + tablet
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import SuperAdminAI from './SuperAdminAI'; // ai_admin_v1
import { Plus, Shield, Users, Radio, BookOpen, Calendar, Edit2, Save, X, Power, ChevronDown, ChevronUp, CreditCard, RefreshCw, Key, CheckCircle, AlertCircle, Trash2, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import TokenHealthCard from '../components/TokenHealthCard'; // token-alert-v1
import SuperAdminBillUsers from './SuperAdminBillUsers'; /* superadmin_bill_users_v1 */


// expiry_bkk_v1: แปลง ISO datetime → date string ตาม timezone BKK
function toBKKDateStr(isoOrDate) {
  if (!isoOrDate) return '';
  const d = new Date(isoOrDate);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(d);
}

const SA = axios.create({ baseURL: window.location.origin });

const SUPER_TOKEN_KEY = 'superadmin_token';

/* bank_accounts_crypto_v1: เครือข่ายที่ใช้บ่อย ให้เลือกจากรายการแทนพิมพ์เอง
   โอนผิดเครือข่าย = เงินหายถาวร การพิมพ์เองจึงเสี่ยงเกินไป
   ยังเผื่อ "อื่นๆ" ไว้ให้พิมพ์ได้ เพราะเชนใหม่เกิดเร็วกว่าที่เราจะตามแก้โค้ด */
const CRYPTO_NETWORKS = ['TRC20', 'ERC20', 'BEP20', 'Polygon', 'Solana', 'Arbitrum', 'Optimism', 'Bitcoin'];

/* superadmin_401_v1: โทเค็นตายได้สองทาง — หมดอายุ (7 วัน) หรือถูกล้มเพราะไปล็อกอิน
   ที่เครื่องอื่น (backend เก็บ active_token_jti ไว้ให้ล็อกอินค้างได้ที่เดียว)

   ของเดิมทุกฟังก์ชันโหลดข้อมูลจับ error ทิ้งเงียบๆ หน้าจึงค้างอยู่ที่แผงเดิม
   โชว์ 0 ทุกช่องพร้อมข้อความ "โหลดข้อมูลไม่ได้" ทั้งที่ความจริงคือหลุดล็อกอินไปแล้ว
   ดูเหมือนข้อมูลหายทั้งระบบ ทั้งที่ข้อมูลอยู่ครบ

   ดักที่ชั้น axios ทีเดียว ครอบทุก request ไม่ต้องไล่แก้ทีละฟังก์ชัน */
SA.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err && err.response && err.response.status === 401) {
      localStorage.removeItem(SUPER_TOKEN_KEY);
      window.dispatchEvent(new Event('superadmin-auth-failed'));
    }
    return Promise.reject(err);
  }
);

// mobile_v1: helper hook สำหรับ detect viewport (mobile + tablet)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showLoginPwd, setShowLoginPwd] = useState(false); /* sa_login_showpwd_v1 */
  const [totp, setTotp] = useState(''); /* superadmin_2fa_v1 */
  const [need2fa, setNeed2fa] = useState(false);
  const [loading, setLoading] = useState(false);
  const isMobile = useIsMobile();

  const handleLogin = async () => {
    setLoading(true);
    try {
      const { data } = await SA.post('/api/superadmin/login', { email, password, totp: totp || undefined }); /* superadmin_2fa_v1 */
      if (data && data.require_2fa) { setNeed2fa(true); setLoading(false); return; }
      localStorage.setItem(SUPER_TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (e) {
      const d = e.response && e.response.data;
      if (d && d.require_2fa) { setNeed2fa(true); toast.error(d.error || 'รหัส 2FA ไม่ถูกต้อง'); }
      else toast.error('ข้อมูลไม่ถูกต้อง');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? 16 : 0,
    }}>
      <div style={{
        background: '#1e293b',
        borderRadius: 16,
        padding: isMobile ? 28 : 40,
        width: isMobile ? '100%' : 380,
        maxWidth: 400,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 24 : 32 }}>
          <Shield size={isMobile ? 40 : 48} color="#0ea5e9" />
          <h1 style={{ margin: '12px 0 4px', color: '#f1f5f9', fontSize: isMobile ? 20 : 22, fontWeight: 700 }}>Super Admin</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>AUTO CHATS Management</p>
        </div>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
          style={{
            width: '100%',
            padding: isMobile ? '13px 14px' : '11px 14px',
            borderRadius: 8,
            border: '1px solid #334155',
            background: '#0f172a', color: '#f1f5f9',
            fontSize: isMobile ? 16 : 14,  // 16px กัน iOS auto-zoom
            marginBottom: 12,
            boxSizing: 'border-box',
            outline: 'none',
          }} />
        {/* sa_login_showpwd_v1: password + eye toggle */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <input type={showLoginPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password"
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{
              width: '100%',
              padding: isMobile ? '13px 44px 13px 14px' : '11px 44px 11px 14px',
              borderRadius: 8,
              border: '1px solid #334155',
              background: '#0f172a', color: '#f1f5f9',
              fontSize: isMobile ? 16 : 14,
              boxSizing: 'border-box',
              outline: 'none',
            }} />
          <button type="button" onClick={() => setShowLoginPwd(v => !v)} tabIndex={-1}
            title={showLoginPwd ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 6, display: 'flex', alignItems: 'center', lineHeight: 0 }}>
            {showLoginPwd ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        {need2fa && (
          <input value={totp} onChange={e => setTotp(e.target.value)} placeholder="รหัส 6 หลักจากแอป (หรือรหัสสำรอง)" autoFocus
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{ width: '100%', padding: isMobile ? '13px 14px' : '11px 14px', borderRadius: 8, border: '1px solid #f59e0b', background: '#0f172a', color: '#f1f5f9', fontSize: isMobile ? 16 : 14, marginBottom: 20, boxSizing: 'border-box', outline: 'none' }} />
        )}
        <button onClick={handleLogin} disabled={loading}
          style={{
            width: '100%',
            padding: isMobile ? '14px' : '12px',
            borderRadius: 8, background: '#0ea5e9', color: '#fff',
            border: 'none', fontSize: 15, fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
          }}>
          {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
        </button>
      </div>
    </div>
  );
}

// ===== custom_domain_v1: TenantDomains — manage custom domains per tenant (groundwork) =====
function TenantDomains({ tenantId, isMobile }) {
  const [domains, setDomains] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [newDomain, setNewDomain] = React.useState('');
  const [adding, setAdding] = React.useState(false);
  const tok = localStorage.getItem('superadmin_token');
  const authH = { headers: { Authorization: 'Bearer ' + tok } };

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await SA.get('/api/superadmin/tenants/' + tenantId + '/domains', authH);
      setDomains(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent — section just shows empty
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  React.useEffect(() => { load(); }, [load]);

  const add = async () => {
    const d = newDomain.trim().toLowerCase();
    if (!d) return;
    setAdding(true);
    try {
      await SA.post('/api/superadmin/tenants/' + tenantId + '/domains', { domain: d }, authH);
      setNewDomain('');
      toast.success('เพิ่มโดเมน ' + d + ' แล้ว');
      load();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.error) || 'เพิ่มไม่สำเร็จ');
    } finally {
      setAdding(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await SA.patch('/api/superadmin/domains/' + id + '/status', { status }, authH);
      load();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.error) || 'เปลี่ยนสถานะไม่สำเร็จ');
    }
  };

  const remove = async (id, domain) => {
    if (!window.confirm('ลบโดเมน ' + domain + '?')) return;
    try {
      await SA.delete('/api/superadmin/domains/' + id, authH);
      toast.success('ลบ ' + domain + ' แล้ว');
      load();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.error) || 'ลบไม่สำเร็จ');
    }
  };

  const statusColor = (s) => ({
    pending: '#f59e0b', verified: '#3b82f6', active: '#10b981', disabled: '#94a3b8'
  })[s] || '#94a3b8';

  return (
    <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: 18, marginBottom: 18 }}>
      <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>🌐 โดเมน (Custom Domain)</h3>
      <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 12px 0' }}>
        ℹ️ บันทึกโดเมนที่ลูกค้าต้องการใช้ — ยังไม่เปิดใช้งานจริง (ต้องตั้ง DNS + SSL เพิ่มภายหลัง)
      </p>

      {loading ? (
        <div style={{ fontSize: 12, color: '#94a3b8' }}>กำลังโหลด...</div>
      ) : domains.length === 0 ? (
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10 }}>ยังไม่มีโดเมน</div>
      ) : (
        <div style={{ marginBottom: 10 }}>
          {domains.map(function (d) {
            return (
              <div key={d.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', marginBottom: 6,
                background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                flexWrap: isMobile ? 'wrap' : 'nowrap'
              }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', flex: 1, minWidth: 0, wordBreak: 'break-all' }}>{d.domain}</span>
                <select value={d.status} onChange={function (e) { changeStatus(d.id, e.target.value); }}
                  style={{
                    fontSize: 11, padding: '3px 6px', borderRadius: 6,
                    border: '1px solid #e2e8f0', color: statusColor(d.status), fontWeight: 600,
                    background: '#fff', cursor: 'pointer'
                  }}>
                  <option value="pending">pending</option>
                  <option value="verified">verified</option>
                  <option value="active">active</option>
                  <option value="disabled">disabled</option>
                </select>
                <button onClick={function () { remove(d.id, d.domain); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#ef4444', flexShrink: 0 }}
                  title="ลบ">
                  <Trash2 size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <input value={newDomain} onChange={function (e) { setNewDomain(e.target.value); }}
          onKeyDown={function (e) { if (e.key === 'Enter') add(); }}
          placeholder="chat.example.com"
          style={{ ...inputStyle(isMobile), flex: 1, minWidth: 0 }} />
        <button onClick={add} disabled={adding || !newDomain.trim()}
          style={{
            padding: isMobile ? '0 16px' : '0 14px',
            borderRadius: 8, background: '#10b981', color: '#fff',
            border: 'none', cursor: (adding || !newDomain.trim()) ? 'not-allowed' : 'pointer',
            fontSize: 13, fontWeight: 600, flexShrink: 0,
            opacity: (adding || !newDomain.trim()) ? 0.5 : 1,
            display: 'flex', alignItems: 'center', gap: 4
          }}>
          <Plus size={14} />{adding ? '...' : 'เพิ่ม'}
        </button>
      </div>
    </div>
  );
}

function LicenseModal({ tenant, onSave, onClose, onSaveProfile, onResetPwd, packages, allTenants }) { /* tenant_referrer_v2 */ /* superadmin_edit_v1 + slip_quota_admin_v3 */
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    plan: tenant.plan || 'basic',
    status: tenant.status || 'active',
    expires_at: toBKKDateStr(tenant.expires_at),
    max_agents: tenant.max_agents || 3,
    max_channels: tenant.max_channels || 3,
    max_kb: tenant.max_kb || 50,
    features: tenant.features || {},
    note: tenant.note || '',
    slip_quota_monthly: tenant.license_slip_quota_monthly || tenant.slip_quota_monthly || 50,
    // feature_flags_v1: tenant-level feature flags (default true)
    has_chat_feature: tenant.has_chat_feature !== false,
    has_bill_feature: tenant.has_bill_feature !== false,
    has_finance_feature: tenant.has_finance_feature === true,
    has_cf_feature: tenant.has_cf_feature === true,
  });

  /* slip_quota_admin_v3: เลือก package → auto-fill ทุก field */
  const applyPackage = (pkgName) => {
    if (!pkgName || !packages) return;
    const pkg = packages.find(p => p.name === pkgName);
    if (!pkg) return;
    let pkgFeatures = pkg.features || {};
    if (typeof pkgFeatures === 'string') {
      try { pkgFeatures = JSON.parse(pkgFeatures); } catch { pkgFeatures = {}; }
    }
    setForm(prev => ({
      ...prev,
      plan: pkg.name,
      max_agents: pkg.max_agents,
      max_channels: pkg.max_channels,
      max_kb: pkg.max_kb,
      features: pkgFeatures,
      slip_quota_monthly: pkg.slip_quota_monthly || 50,
      expires_at: pkg.duration_days
        ? toBKKDateStr(new Date(Date.now() + pkg.duration_days * 24 * 60 * 60 * 1000))
        : prev.expires_at,
    }));
    toast.success(`✅ ใช้ template "${pkg.name}" เรียบร้อย — ปรับแก้ได้ก่อนบันทึก`);
  };

  /* tenant_referrer_v2: include referred_by */
  /* referrer_percent_override_v1: include override + use_default flag */
  /* bill_expiry_admin_ui_v1 */
  const [billExp, setBillExp] = React.useState('');
  const [billSaving, setBillSaving] = React.useState(false);
  React.useEffect(function () {
    var tok = localStorage.getItem('superadmin_token');
    SA.get('/api/superadmin/tenants/' + tenant.id + '/bill-license', { headers: { Authorization: 'Bearer ' + tok } })
      .then(function (r) { if (r.data && r.data.expires_at) setBillExp(toBKKDateStr(r.data.expires_at)); })
      .catch(function () {});
  }, []);
  var saveBillExpiry = async function () {
    if (!billExp) { toast.error('เลือกวันหมดอายุบิลก่อน'); return; }
    setBillSaving(true);
    try {
      var tok = localStorage.getItem('superadmin_token');
      await SA.put('/api/superadmin/tenants/' + tenant.id + '/bill-license', { expires_at: billExp }, { headers: { Authorization: 'Bearer ' + tok } });
      toast.success('บันทึกวันหมดอายุระบบบิลแล้ว');
    } catch (e) { toast.error('บันทึกไม่สำเร็จ'); }
    finally { setBillSaving(false); }
  };

  /* referral_duration_v1: per-referral commission window (เก็บบน tenant ที่ถูกแนะนำ) */
  const [refMonths, setRefMonths] = React.useState('');
  const [refStart, setRefStart] = React.useState('');
  const [refDefault, setRefDefault] = React.useState('4');
  const [refEndsAt, setRefEndsAt] = React.useState(null);
  const [refSaving, setRefSaving] = React.useState(false);
  React.useEffect(function () {
    var tok = localStorage.getItem('superadmin_token');
    SA.get('/api/superadmin/tenants/' + tenant.id + '/referral-commission', { headers: { Authorization: 'Bearer ' + tok } })
      .then(function (r) {
        if (r.data) {
          setRefMonths(r.data.ref_commission_months != null ? String(r.data.ref_commission_months) : '');
          setRefStart(r.data.ref_commission_start ? toBKKDateStr(r.data.ref_commission_start) : '');
          setRefDefault(r.data.default_months != null ? String(r.data.default_months) : '4');
          setRefEndsAt(r.data.ends_at || null);
        }
      })
      .catch(function () {});
  }, []);
  var saveRefCommission = async function () {
    setRefSaving(true);
    try {
      var tok = localStorage.getItem('superadmin_token');
      var body = { months: refMonths === '' ? null : parseInt(refMonths, 10), start: refStart === '' ? null : refStart };
      await SA.put('/api/superadmin/tenants/' + tenant.id + '/referral-commission', body, { headers: { Authorization: 'Bearer ' + tok } });
      toast.success('บันทึกระยะเวลาค่าคอมแล้ว');
      var g = await SA.get('/api/superadmin/tenants/' + tenant.id + '/referral-commission', { headers: { Authorization: 'Bearer ' + tok } });
      if (g.data) setRefEndsAt(g.data.ends_at || null);
    } catch (e) { toast.error('บันทึกไม่สำเร็จ'); }
    finally { setRefSaving(false); }
  };

  const [profileForm, setProfileForm] = React.useState({
    name: tenant.name || '',
    email: tenant.email || '',
    note: tenant.note || '',
    referred_by: tenant.referred_by || '',
    referral_percent_override: (tenant.referral_percent_override !== null && tenant.referral_percent_override !== undefined) ? String(tenant.referral_percent_override) : '',
    use_default_percent: (tenant.referral_percent_override === null || tenant.referral_percent_override === undefined)
  });
  const [savingProfile] = React.useState(false);
  const [newPwd, setNewPwd] = React.useState('');
  const [showPwd, setShowPwd] = React.useState(false);
  const [resettingPwd] = React.useState(false);

  React.useEffect(() => {
    /* tenant_referrer_v2: include referred_by in reset */
    /* referrer_percent_override_v1: include override in reset */
    setProfileForm({
      name: tenant.name || '',
      email: tenant.email || '',
      note: tenant.note || '',
      referred_by: tenant.referred_by || '',
      referral_percent_override: (tenant.referral_percent_override !== null && tenant.referral_percent_override !== undefined) ? String(tenant.referral_percent_override) : '',
      use_default_percent: (tenant.referral_percent_override === null || tenant.referral_percent_override === undefined)
    });
    setNewPwd('');
  }, [tenant]);

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: isMobile ? 'flex-end' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff',
        borderRadius: isMobile ? '16px 16px 0 0' : 16,
        padding: isMobile ? 20 : 32,
        width: isMobile ? '100%' : 520,
        maxHeight: isMobile ? '92vh' : '90vh',
        overflowY: 'auto',
      }}>
        {isMobile && <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 16px' }} />}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 16 : 24 }}>
          <h2 style={{
            margin: 0,
            fontSize: isMobile ? 16 : 18,
            fontWeight: 700,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            paddingRight: 12,
          }}>⚙️ License: {tenant.name}</h2>

          {/* license_modal_consolidated_v1: Template box removed - Plan dropdown is now the unified selector */}
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
            <X size={isMobile ? 22 : 20} />
          </button>
        </div>

        {/* Plan + Status — 1 col บน mobile */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? 12 : 14,
          marginBottom: isMobile ? 12 : 14,
        }}>
          <div>
            <Label>Plan</Label>
            {/* license_modal_consolidated_v1: dynamic options from packages + auto-fill on change */}
            <select
              value={form.plan}
              onChange={e => {
                const v = e.target.value;
                if (!v) return;
                // Try to find matching package (case-insensitive) and apply it
                const pkg = packages && packages.find(p => p.name.toLowerCase() === v.toLowerCase());
                if (pkg) {
                  applyPackage(pkg.name);
                } else {
                  setForm({ ...form, plan: v });
                }
              }}
              style={inputStyle(isMobile)}>
              {packages && packages.filter(p => p.is_active).map(p => (
                <option key={p.id} value={p.name.toLowerCase()}>{p.name}</option>
              ))}
              {/* fallback: ถ้า license.plan ปัจจุบันไม่ match package ใดเลย — ใส่ option ค้างไว้ */}
              {form.plan && packages && !packages.find(p => p.name.toLowerCase() === form.plan.toLowerCase()) && (
                <option value={form.plan}>{form.plan} (legacy)</option>
              )}
            </select>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              💡 เลือก plan → auto-fill limits ด้านล่าง ปรับแก้ได้ก่อนกดบันทึก
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
              style={inputStyle(isMobile)}>
              <option value="active">Active ✅</option>
              <option value="suspended">Suspended ⏸️</option>
              <option value="expired">Expired ❌</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: isMobile ? 12 : 14 }}>
          <Label>วันหมดอายุ</Label>
          <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })}
            style={inputStyle(isMobile)} />
        </div>

        {/* Limits — 1 col บน mobile */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
          gap: isMobile ? 12 : 14,
          marginBottom: isMobile ? 12 : 14,
        }}>
          <div>
            <Label><Radio size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />Social Accounts</Label>
            <input type="number" min="1" max="50" value={form.max_channels}
              onChange={e => setForm({ ...form, max_channels: parseInt(e.target.value) })}
              style={inputStyle(isMobile)} />
          </div>
          <div>
            <Label><Users size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />จำนวน Admin</Label>
            <input type="number" min="1" max="100" value={form.max_agents}
              onChange={e => setForm({ ...form, max_agents: parseInt(e.target.value) })}
              style={inputStyle(isMobile)} />
          </div>
          <div>
            <Label><BookOpen size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />จำนวน KB</Label>
            <input type="number" min="1" max="1000" value={form.max_kb}
              onChange={e => setForm({ ...form, max_kb: parseInt(e.target.value) })}
              style={inputStyle(isMobile)} />
          </div>
        </div>

        {/* feature_flags_v1: tenant-level feature gates (chat / bill) */}
        <div style={{ marginBottom: isMobile ? 12 : 14 }}>
          <Label>ระบบที่เปิดใช้ (Tenant-level)</Label>
          <div style={{
            marginTop: 4,
            display: isMobile ? 'grid' : 'block',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'none',
            gap: isMobile ? 8 : 0,
          }}>
            {[['has_chat_feature', '💬 ระบบแชท (Inbox, Channels, Agents)'], ['has_bill_feature', '🧾 ระบบบิล (Invoices, Products, Customers)'], ['has_finance_feature', '💰 ระบบการเงิน (รายรับ-จ่าย, ธุรกิจ)'], ['has_cf_feature', '📹 ไลฟ์ขาย (CF)']].map(([k, l]) => {
              const checked = !!form[k];
              return (
                <label key={k} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: isMobile ? '12px 12px' : '4px 0',
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 13,
                  borderRadius: isMobile ? 8 : 0,
                  border: isMobile ? `2px solid ${checked ? '#0ea5e9' : '#e2e8f0'}` : 'none',
                  background: isMobile ? (checked ? '#f0f9ff' : '#f8fafc') : 'transparent',
                  color: isMobile && checked ? '#0ea5e9' : '#1e293b',
                  fontWeight: isMobile && checked ? 600 : 400,
                  transition: 'all 0.15s',
                  marginBottom: isMobile ? 0 : 4,
                }}>
                  <input type="checkbox" checked={checked}
                    onChange={e => setForm({ ...form, [k]: e.target.checked })}
                    style={{
                      width: isMobile ? 18 : 16,
                      height: isMobile ? 18 : 16,
                      accentColor: '#0ea5e9',
                      flexShrink: 0,
                      cursor: 'pointer',
                    }} />
                  <span>{l}</span>
                </label>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>
            ⚠️ ต้องเปิดอย่างน้อย 1 ระบบ
          </p>
        </div>

        {/* bill_expiry_admin_ui_v1: จัดการวันหมดอายุระบบบิล (แยกจาก license แชท) */}
        <div style={{ marginBottom: isMobile ? 12 : 14, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <Label>🧾 วันหมดอายุระบบบิล</Label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
            <input type="date" value={billExp} onChange={e => setBillExp(e.target.value)}
              style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }} />
            <button type="button" onClick={saveBillExpiry} disabled={billSaving}
              style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: billSaving ? 0.6 : 1 }}>
              {billSaving ? 'กำลังบันทึก...' : 'บันทึกวันหมดอายุบิล'}
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', margin: '6px 0 0' }}>
            แยกจากวันหมดอายุแชท · ระบบแจ้งเตือน Telegram อัตโนมัติ 7/3/1/0 วัน
          </p>
        </div>

        {/* Features — mobile_v2: clickable cards เพื่อให้ checkbox เด่นชัดบน mobile */}
        <div style={{ marginBottom: isMobile ? 12 : 14 }}>
          <Label>Features</Label>
          <div style={{
            marginTop: 4,
            display: isMobile ? 'grid' : 'block',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'none',
            gap: isMobile ? 8 : 0,
          }}>
            {['bot_training', 'kpi_dashboard', 'quick_replies', 'multi_channel'].map(f => {
              const checked = !!form.features[f];
              const labelText = f === 'bot_training' ? '🤖 Bot Training' :
                f === 'kpi_dashboard' ? '📊 KPI Dashboard' :
                f === 'quick_replies' ? '⚡ Quick Replies' : '📡 Multi Channel';
              return (
                <label key={f} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: isMobile ? '12px 12px' : '4px 0',
                  cursor: 'pointer',
                  fontSize: isMobile ? 13 : 13,
                  borderRadius: isMobile ? 8 : 0,
                  border: isMobile ? `2px solid ${checked ? '#0ea5e9' : '#e2e8f0'}` : 'none',
                  background: isMobile ? (checked ? '#f0f9ff' : '#f8fafc') : 'transparent',
                  color: isMobile && checked ? '#0ea5e9' : '#1e293b',
                  fontWeight: isMobile && checked ? 600 : 400,
                  transition: 'all 0.15s',
                }}>
                  <input type="checkbox" checked={checked}
                    onChange={e => setForm({ ...form, features: { ...form.features, [f]: e.target.checked } })}
                    style={{
                      width: isMobile ? 18 : 16,
                      height: isMobile ? 18 : 16,
                      accentColor: '#0ea5e9',
                      flexShrink: 0,
                      cursor: 'pointer',
                    }} />
                  <span style={{ flex: 1 }}>{labelText}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* slip_quota_admin_v3: Slip Verification Quota */}
        <div style={{ marginBottom: isMobile ? 14 : 16, padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
          <Label>💳 Slip Quota / เดือน</Label>
          <input type="number" min="0" max="100000"
            value={form.slip_quota_monthly}
            onChange={e => setForm({ ...form, slip_quota_monthly: parseInt(e.target.value) || 0 })}
            style={inputStyle(isMobile)} />
          <div style={{ fontSize: 11, color: '#16a34a', marginTop: 6 }}>
            🔁 รีเซ็ตอัตโนมัติทุก 1 เดือนตามรอบของแต่ละ tenant — Default 50
          </div>
        </div>

        <div style={{ marginBottom: isMobile ? 18 : 24 }}>
          <Label>หมายเหตุ</Label>
          <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
            rows={isMobile ? 3 : 2} placeholder="บันทึกเพิ่มเติม..."
            style={{ ...inputStyle(isMobile), resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        {/* Tenant Profile section */}
        <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: 18, marginBottom: 18 }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>✏️ ข้อมูล Tenant</h3>
          <div style={{ marginBottom: 10 }}>
            <Label>ชื่อ Tenant</Label>
            <input type="text" value={profileForm.name}
              onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
              style={inputStyle(isMobile)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Label>Email</Label>
            <input type="email" value={profileForm.email}
              onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
              style={inputStyle(isMobile)} />
          </div>
          {/* tenant_referrer_v2: ผู้แนะนำ dropdown */}
          <div style={{ marginBottom: 12 }}>
            <Label>👥 ผู้แนะนำ</Label>
            <select value={profileForm.referred_by || ''}
              onChange={e => setProfileForm({ ...profileForm, referred_by: e.target.value })}
              style={inputStyle(isMobile)}>
              <option value="">— ไม่มีผู้แนะนำ —</option>
              {(allTenants || []).filter(t => t.id !== tenant.id).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {tenant.referral_code && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                💳 Referral code: <strong>{tenant.referral_code}</strong>
              </div>
            )}
            <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6, padding: 6, background: '#1e293b', borderRadius: 4 }}>
              ⚠️ การตั้ง/เปลี่ยนผู้แนะนำจะสร้าง pending referral ใหม่ในระบบ — ถ้าลูกค้ารายนี้จ่ายเงิน package ถัดไป ผู้แนะนำจะได้ credit อัตโนมัติ
            </div>
          </div>
          {/* referral_duration_v1: ระยะเวลารับค่าคอม (แสดงเมื่อ tenant นี้มีผู้แนะนำ) */}
          {profileForm.referred_by && (
            <div style={{ marginBottom: 12, padding: 10, background: '#0f172a', borderRadius: 8, border: '1px solid #334155' }}>
              <Label>⏳ ระยะเวลาที่ผู้แนะนำได้รับค่าคอมจาก {tenant.name}</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <input type="number" min="0" max="60" value={refMonths}
                  onChange={e => setRefMonths(e.target.value)}
                  placeholder={'ค่ากลาง ' + refDefault}
                  style={{ ...inputStyle(isMobile), maxWidth: 140 }} />
                <span style={{ color: '#94a3b8', fontSize: 14 }}>เดือน</span>
              </div>
              <div style={{ marginTop: 8 }}>
                <Label>📅 วันเริ่มนับค่าคอม (จากการจ่ายครั้งแรก)</Label>
                <input type="date" value={refStart}
                  onChange={e => setRefStart(e.target.value)}
                  style={{ ...inputStyle(isMobile), maxWidth: 200 }} />
              </div>
              {refEndsAt && (
                <div style={{ fontSize: 12, color: '#38bdf8', marginTop: 6 }}>
                  🔔 ค่าคอมสิ้นสุด: <strong>{toBKKDateStr(refEndsAt)}</strong>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                ℹ️ เว้นว่างช่องเดือน = ใช้ค่ากลาง ({refDefault} เดือน). referral ใหม่ระบบตั้ง “วันเริ่มนับ” อัตโนมัติตอนจ่ายครั้งแรก — referral เดิมตั้งวันเริ่มเองที่นี่เพื่อเริ่มจ่ายค่าคอม
              </div>
              <button onClick={saveRefCommission} disabled={refSaving}
                style={{ marginTop: 8, padding: '8px 16px', borderRadius: 8, background: '#0ea5e9', color: '#fff', border: 'none', cursor: refSaving ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {refSaving ? 'กำลังบันทึก...' : '💾 บันทึกระยะเวลาค่าคอม'}
              </button>
            </div>
          )}
          {/* referrer_percent_override_v1: ส่วนลด % พิเศษ */}
          <div style={{ marginBottom: 12, padding: 10, background: '#0f172a', borderRadius: 8, border: '1px solid #334155' }}>
            <Label>✨ ส่วนลด % พิเศษ (เมื่อ {tenant.name} ชวนเพื่อน)</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={profileForm.use_default_percent || false}
                  onChange={e => setProfileForm({
                    ...profileForm,
                    use_default_percent: e.target.checked,
                    referral_percent_override: e.target.checked ? '' : profileForm.referral_percent_override
                  })} />
                ใช้ default
              </label>
              <input type="number" min="0" max="100"
                value={profileForm.referral_percent_override}
                disabled={profileForm.use_default_percent}
                onChange={e => setProfileForm({
                  ...profileForm,
                  referral_percent_override: e.target.value,
                  use_default_percent: false
                })}
                placeholder="เช่น 15"
                style={{ ...inputStyle(isMobile), maxWidth: 100, opacity: profileForm.use_default_percent ? 0.5 : 1 }} />
              <span style={{ color: '#94a3b8', fontSize: 14 }}>%</span>
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
              ℹ️ % นี้ใช้ตอน {tenant.name} ชวนเพื่อนใหม่ (ถ้าใช้ default จะใช้ค่าจากระบบ)
            </div>
          </div>
          <button onClick={() => onSaveProfile(profileForm)} disabled={savingProfile}
            style={{
              width: isMobile ? '100%' : 'auto',
              padding: isMobile ? '12px 16px' : '8px 16px',
              borderRadius: 8, background: '#10b981', color: '#fff',
              border: 'none', cursor: savingProfile ? 'wait' : 'pointer',
              fontSize: isMobile ? 14 : 13, fontWeight: 600,
            }}>
            {savingProfile ? 'กำลังบันทึก...' : '💾 บันทึกข้อมูล Tenant'}
          </button>
        </div>

        {/* Password Reset section */}
        <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: 18, marginBottom: 18 }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>🔑 รีเซ็ตรหัสผ่าน</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input type={showPwd ? 'text' : 'password'} value={newPwd}
              onChange={e => setNewPwd(e.target.value)}
              placeholder="รหัสใหม่ (อย่างน้อย 6 ตัว)"
              style={{ ...inputStyle(isMobile), flex: 1, minWidth: 0 }} />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              style={{
                padding: isMobile ? '0 14px' : '0 12px',
                borderRadius: 8, border: '1px solid #e2e8f0',
                background: '#fff', cursor: 'pointer',
                fontSize: isMobile ? 16 : 13,
                flexShrink: 0,
              }}>
              {showPwd ? '🙈' : '👁️'}
            </button>
            <button type="button" onClick={() => {
              const chars = 'ABCDEFGHIJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
              let p = '';
              for (let i = 0; i < 12; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
              setNewPwd(p);
              setShowPwd(true);
            }}
              title="สร้างรหัสสุ่ม"
              style={{
                padding: isMobile ? '0 14px' : '0 12px',
                borderRadius: 8, border: '1px solid #e2e8f0',
                background: '#fff', cursor: 'pointer',
                fontSize: isMobile ? 16 : 13,
                flexShrink: 0,
              }}>
              🎲
            </button>
          </div>
          <button onClick={() => onResetPwd(newPwd)} disabled={!newPwd || newPwd.length < 6 || resettingPwd}
            style={{
              width: isMobile ? '100%' : 'auto',
              padding: isMobile ? '12px 16px' : '8px 16px',
              borderRadius: 8, background: '#dc2626', color: '#fff',
              border: 'none',
              cursor: (!newPwd || newPwd.length < 6 || resettingPwd) ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? 14 : 13, fontWeight: 600,
              opacity: (!newPwd || newPwd.length < 6) ? 0.5 : 1,
            }}>
            {resettingPwd ? 'กำลังรีเซ็ต...' : '🔑 รีเซ็ตรหัสผ่าน'}
          </button>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>⚠️ การรีเซ็ตรหัสจะมีผลทันที กรุณาแจ้ง Tenant ทราบ</p>
        </div>

        {/* custom_domain_v1: domain management section */}
        <TenantDomains tenantId={tenant.id} isMobile={isMobile} />

        {/* Bottom actions */}
        <div style={{
          display: 'flex',
          gap: 10,
          justifyContent: isMobile ? 'stretch' : 'flex-end',
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          <button onClick={onClose}
            style={{
              padding: isMobile ? '12px 20px' : '10px 20px',
              borderRadius: 8, background: '#f1f5f9', border: 'none',
              cursor: 'pointer', fontSize: 14, fontWeight: 500,
              order: isMobile ? 2 : 1,
            }}>ยกเลิก</button>
          <button onClick={() => onSave(form)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: isMobile ? '12px 24px' : '10px 24px',
              borderRadius: 8, background: '#0ea5e9', color: '#fff',
              border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              order: isMobile ? 1 : 2,
            }}>
            <Save size={14} />บันทึก License
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(SUPER_TOKEN_KEY));
  const [tenants, setTenants] = useState([]);
  // sortField_v1: header click to sort
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const handleSort = (field) => {
    if (sortField !== field) { setSortField(field); setSortOrder('asc'); return; }
    if (sortOrder === 'asc') { setSortOrder('desc'); return; }
    setSortField(null); setSortOrder('asc'); // 3rd click = reset
  };
  const PLAN_ORDER = { 'TRIAL': 1, 'BASIC': 2, 'PRO': 3, 'PREMIUM': 4, 'ENTERPRISE': 5, 'UNLIMITED': 6 };
  const STATUS_ORDER = { 'active': 1, 'expired': 2, 'suspended': 3 };
  const sortedTenants = useMemo(() => {
    if (!sortField) return tenants;
    return [...tenants].sort((a, b) => {
      let av, bv;
      if (sortField === 'plan') {
        av = PLAN_ORDER[(a.plan_code || a.plan || '').toUpperCase()] || 99;
        bv = PLAN_ORDER[(b.plan_code || b.plan || '').toUpperCase()] || 99;
      } else if (sortField === 'status') {
        av = STATUS_ORDER[(a.status || '').toLowerCase()] || 99;
        bv = STATUS_ORDER[(b.status || '').toLowerCase()] || 99;
      } else if (sortField === 'expires_at') {
        av = a.expires_at ? new Date(a.expires_at).getTime() : Infinity;
        bv = b.expires_at ? new Date(b.expires_at).getTime() : Infinity;
      } else {
        av = a[sortField]; bv = b[sortField];
      }
      if (av < bv) return sortOrder === 'asc' ? -1 : 1;
      if (av > bv) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tenants, sortField, sortOrder]);
  const [quotaTenant, setQuotaTenant] = useState(null); // slip_quota_admin_v1
  const [packages, setPackages] = useState([]);
  const [editPkg, setEditPkg] = useState(null);
  const [showPkgForm, setShowPkgForm] = useState(false);
  const emptyPkg = { name: '', price: 0, duration_days: 30, max_channels: 1, max_agents: 1, max_kb: 20, slip_quota_monthly: 50, max_telegrams: 1, description: '', features: { bot_training: false, telegram_notify: false, kpi_dashboard: false, quick_replies: false, chat: false, bill: false, finance: false } /* pkg_feature_gates_v1 */, is_active: true, sort_order: 0 };
  const [pkgForm, setPkgForm] = useState(emptyPkg);
  const [pending, setPending] = useState([]);
  const [payments, setPayments] = useState([]); // payments_v1
  const [activeTab, setActiveTab] = useState('tenants');
  /* platform_brand_v1 */
  const [pbrands, setPbrands] = useState([]);
  const [pbForm, setPbForm] = useState({ domain: '', brand_name: '' });
  const pbAuth = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('superadmin_token') } });
  const loadPbrands = () => { SA.get('/api/superadmin/platform-brands', pbAuth()).then(r => setPbrands(r.data)).catch(() => {}); };
  useEffect(() => { if (activeTab === 'brands') { loadPbrands(); loadPtenants(); } /* eslint-disable-next-line */ }, [activeTab]);
  const setPbField = (i, k, v) => setPbrands(a => a.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const addPbrand = () => { if (!pbForm.domain || !pbForm.brand_name) return alert('กรอกโดเมนและชื่อแบรนด์'); SA.post('/api/superadmin/platform-brands', pbForm, pbAuth()).then(() => { setPbForm({ domain: '', brand_name: '' }); loadPbrands(); }).catch(e => alert((e.response && e.response.data && e.response.data.error) || 'ล้มเหลว')); };
  const savePbrand = (b) => { SA.put('/api/superadmin/platform-brands/' + b.id, b, pbAuth()).then(loadPbrands).catch(() => alert('ล้มเหลว')); };
  const delPbrand = (id) => { if (!window.confirm('ลบโดเมนนี้?')) return; SA.delete('/api/superadmin/platform-brands/' + id, pbAuth()).then(loadPbrands).catch(() => {}); };
  /* alert_domain_v1 */
  const [ptenants, setPtenants] = useState([]);
  const loadPtenants = () => { SA.get('/api/superadmin/platform-brands/tenants', pbAuth()).then(r => setPtenants(r.data)).catch(() => {}); };
  const setTenantDomain = (id, domain) => { SA.put('/api/superadmin/platform-brands/tenant/' + id, { app_domain: domain }, pbAuth()).then(loadPtenants).catch(() => alert('ล้มเหลว')); };
  const [editTenant, setEditTenant] = useState(null);
  // delete_tenant_v1: state สำหรับ delete confirmation modal
  const [deleteTenant, setDeleteTenant] = useState(null);
  const isMobile = useIsMobile();

  const loadPackages = async (tok) => {
    try {
      const { data } = await SA.get('/api/packages', { headers: { Authorization: `Bearer ${tok}` } });
      setPackages(data);
    } catch {}
  };

  const loadPending = async (tok) => {
    try {
      const { data } = await SA.get('/api/superadmin/pending', { headers: { Authorization: `Bearer ${tok}` } });
      setPending(data);
    } catch {}
  }

  // payments_v1: โหลด pending payment requests
  const loadPayments = async (tok) => {
    try {
      const { data } = await SA.get('/api/superadmin/payment-requests', { headers: { Authorization: `Bearer ${tok}` } });
      setPayments(data || []);
    } catch (err) { console.error('loadPayments failed:', err.message); }
  };

  const handleApprovePayment = async (id) => {
    if (!window.confirm('ยืนยันอนุมัติการชำระเงินนี้?\nระบบจะต่ออายุ license อัตโนมัติ')) return;
    try {
      await SA.post(`/api/superadmin/payment-requests/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('อนุมัติสำเร็จ — ต่ออายุ license แล้ว');
      loadPayments(token); loadTenants(token);
    } catch (err) { toast.error('อนุมัติไม่สำเร็จ: ' + (err.response?.data?.error || err.message)); }
  };

  const handleRejectPayment = async (id) => {
    const reason = window.prompt('เหตุผลที่ปฏิเสธ:');
    if (!reason || reason.trim().length < 3) { toast.error('กรุณาระบุเหตุผล (≥3 ตัวอักษร)'); return; }
    try {
      await SA.post(`/api/superadmin/payment-requests/${id}/reject`, { reason: reason.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('ปฏิเสธแล้ว');
      loadPayments(token);
    } catch (err) { toast.error('ปฏิเสธไม่สำเร็จ: ' + (err.response?.data?.error || err.message)); }
  };
;

  const loadTenants = async (tok) => {
    try {
      const { data } = await SA.get('/api/superadmin/tenants', { headers: { Authorization: `Bearer ${tok}` } });
      setTenants(data);
    } catch (err) {
      /* superadmin_401_v1: 401 มี interceptor พากลับหน้าล็อกอินให้แล้ว
         ไม่ต้องขึ้น "โหลดข้อมูลไม่ได้" ซ้ำ เพราะไม่ใช่ความผิดของการโหลด */
      if (!(err && err.response && err.response.status === 401)) toast.error('โหลดข้อมูลไม่ได้');
    }
  };

  useEffect(() => { if (token) { loadTenants(token); loadPending(token); loadPackages(token); loadPayments(token); } }, [token]);

  /* superadmin_401_v1: โทเค็นตาย -> กลับไปหน้าล็อกอินทันที แทนที่จะค้างอยู่หน้าเปล่า */
  const tokenRef = React.useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => {
    const onAuthFailed = () => {
      /* หน้าล็อกอินก็ยิงผ่าน SA ตัวเดียวกัน กรอกรหัสผิดจะได้ 401 เหมือนกัน
         ตอนนั้นยังไม่มีโทเค็นอยู่แล้ว ไม่ต้องขึ้นข้อความซ้อนกับ "ข้อมูลไม่ถูกต้อง" */
      if (!tokenRef.current) return;
      tokenRef.current = null;
      toast.error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
      setToken(null);
    };
    window.addEventListener('superadmin-auth-failed', onAuthFailed);
    return () => window.removeEventListener('superadmin-auth-failed', onAuthFailed);
  }, []);

  // superadmin_edit_v1: handlers
  /* referrer_percent_override_v1: handleSaveProfile — supports per-tenant % + update_pending prompt */
  const handleSaveProfile = async (profileForm) => {
    if (!profileForm.name || !profileForm.email) { toast.error('กรุณากรอกชื่อและอีเมล'); return; }
    // Compute new override value
    const newOverride = profileForm.use_default_percent
      ? null
      : (profileForm.referral_percent_override !== '' && profileForm.referral_percent_override !== null && profileForm.referral_percent_override !== undefined
          ? parseInt(profileForm.referral_percent_override, 10)
          : null);
    if (newOverride !== null && (isNaN(newOverride) || newOverride < 0 || newOverride > 100)) {
      toast.error('ส่วนลด % ต้องอยู่ระหว่าง 0-100');
      return;
    }
    // Detect change vs current
    const oldOverride = (editTenant.referral_percent_override !== null && editTenant.referral_percent_override !== undefined)
      ? parseInt(editTenant.referral_percent_override, 10) : null;
    const percentChanged = (oldOverride !== newOverride);
    let updatePending = false;
    if (percentChanged) {
      try {
        const { data: cnt } = await SA.get(`/api/superadmin/tenants/${editTenant.id}/pending-referrals-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cnt && cnt.count > 0) {
          updatePending = window.confirm(
            `${editTenant.name} มี pending referrals ${cnt.count} รายการ\n\n` +
            `ต้องการอัปเดต % ของ pending ทั้งหมดเป็นค่าใหม่ด้วยหรือไม่?\n\n` +
            `[OK] = อัปเดต pending ด้วย\n[Cancel] = คงค่าเดิมไว้`
          );
        }
      } catch (e) { /* non-fatal */ }
    }
    // Build clean PATCH body
    const body = {
      name: profileForm.name,
      email: profileForm.email,
      note: profileForm.note,
      referred_by: profileForm.referred_by || null,
      referral_percent_override: newOverride,
      update_pending: updatePending
    };
    try {
      await SA.patch(`/api/superadmin/tenants/${editTenant.id}/profile`, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('✅ บันทึกข้อมูล Tenant สำเร็จ' + (updatePending ? ' (อัปเดต pending แล้ว)' : ''));
      loadTenants(token);
      setEditTenant(prev => prev ? { ...prev, ...body } : prev);
    } catch (err) {
      toast.error(err.response?.data?.error || 'บันทึกไม่สำเร็จ');
    }
  };

  const handleResetPwd = async (newPwd) => {
    if (!newPwd || newPwd.length < 6) { toast.error('รหัสต้องมีอย่างน้อย 6 ตัว'); return; }
    if (!window.confirm(`รีเซ็ตรหัสผ่านของ ${editTenant.name}?\n\nรหัสใหม่: ${newPwd}\n\nยืนยันการเปลี่ยน?`)) return;
    try {
      await SA.post(`/api/superadmin/tenants/${editTenant.id}/reset-password`, { new_password: newPwd }, { headers: { Authorization: `Bearer ${token}` } });
      navigator.clipboard.writeText(newPwd).catch(() => {});
      toast.success(`✅ รีเซ็ตรหัสสำเร็จ! รหัสใหม่: ${newPwd} (คัดลอกไว้แล้ว)`, { duration: 10000 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'รีเซ็ตไม่สำเร็จ');
    }
  };

  const handleSaveLicense = async (form) => {
    // feature_flags_v1: validate at least one feature enabled
    if (!form.has_chat_feature && !form.has_bill_feature) {
      toast.error('ต้องเปิดอย่างน้อย 1 ระบบ (แชท หรือ บิล)');
      return;
    }
    try {
      await SA.put(`/api/superadmin/tenants/${editTenant.id}/license`, form, { headers: { Authorization: `Bearer ${token}` } });
      // feature_flags_v1: persist tenant-level feature flags (separate endpoint)
      await SA.patch(`/api/superadmin/tenants/${editTenant.id}/features`, {
        has_chat_feature: !!form.has_chat_feature,
        has_bill_feature: !!form.has_bill_feature,
        has_finance_feature: !!form.has_finance_feature,
        has_cf_feature: !!form.has_cf_feature,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('บันทึกสำเร็จ');
      setEditTenant(null);
      loadTenants(token);
    } catch { toast.error('บันทึกไม่สำเร็จ'); }
  };

  // delete_tenant_v1: เรียก DELETE endpoint พร้อม confirm_name
  const handleDeleteTenant = async (tenant, confirmName) => {
    try {
      const res = await SA.delete(`/api/superadmin/tenants/${tenant.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { confirm_name: confirmName }
      });
      toast.success(`ลบ "${tenant.name}" สำเร็จ — backup: ${res.data.files_deleted} ไฟล์`);
      setDeleteTenant(null);
      loadTenants(token);
    } catch (err) {
      const msg = err?.response?.data?.error || 'ลบไม่สำเร็จ';
      const hint = err?.response?.data?.hint;
      toast.error(hint ? `${msg}: ${hint}` : msg);
    }
  };

  // combined_view_v1: ต่ออายุแชท+บิลพร้อมกัน (t = tenant object)
  const handleRenew = async (t, days = 30) => {
    const both = t && t.has_bill_feature;
    if (!window.confirm(both ? `ต่ออายุทั้งแชทและบิล ${days} วัน?` : `ต่ออายุ ${days} วัน?`)) return;
    try {
      const res = await axios.post(`/api/superadmin/tenants/${t.id}/renew-all`, { days }, { headers: { Authorization: `Bearer ${token}` } });
      const parts = [];
      if (res.data.chat) parts.push(`แชท → ${new Date(res.data.chat.new_expires).toLocaleDateString('th-TH')}`);
      if (res.data.bill) parts.push(`บิล → ${new Date(res.data.bill.new_expires).toLocaleDateString('th-TH')}`);
      toast.success(parts.length ? `ต่ออายุสำเร็จ! ${parts.join(' · ')}` : 'ต่ออายุสำเร็จ!');
      loadTenants(token);
    } catch (err) { toast.error('ต่ออายุไม่สำเร็จ'); }
  };

  const handleToggle = async (id) => {
    try {
      await SA.patch(`/api/superadmin/tenants/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      loadTenants(token);
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const handleSavePkg = async () => {
    try {
      if (editPkg?.id) {
        await SA.put(`/api/packages/${editPkg.id}`, pkgForm, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await SA.post('/api/packages', pkgForm, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('บันทึกสำเร็จ'); setShowPkgForm(false); setEditPkg(null); setPkgForm(emptyPkg); loadPackages(token);
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const handleDeletePkg = async (id) => {
    if (!window.confirm('ลบแพ็คเกจนี้?')) return;
    try {
      await SA.delete(`/api/packages/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('ลบแล้ว'); loadPackages(token);
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const handleApprove = async (id) => {
    try {
      await SA.post(`/api/superadmin/tenants/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('อนุมัติสำเร็จ'); loadTenants(token); loadPending(token);
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const handleReject = async (id) => {
    if (!window.confirm('ยืนยันการปฏิเสธ?')) return;
    try {
      await SA.post(`/api/superadmin/tenants/${id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('ปฏิเสธแล้ว'); loadPending(token);
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const statusColor = (s) => s === 'active' ? '#22c55e' : s === 'suspended' ? '#f59e0b' : '#ef4444';
  const planColor = (p) => p === 'enterprise' ? '#a855f7' : p === 'pro' ? '#0ea5e9' : p === 'basic' ? '#22c55e' : '#94a3b8';

  if (!token) return <LoginPage onLogin={setToken} />;

  return (
    <div style={{
      padding: isMobile ? '16px 12px' : '24px 28px',
      background: '#0f172a',
      minHeight: '100vh',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 18 : 28,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Shield size={isMobile ? 24 : 28} color="#0ea5e9" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#f1f5f9' }}>Super Admin Panel</h1>
            <p style={{ margin: 0, fontSize: isMobile ? 12 : 13, color: '#64748b' }}>จัดการ Tenant & License</p>
          </div>
        </div>
        <button onClick={() => { localStorage.removeItem(SUPER_TOKEN_KEY); setToken(null); }}
          style={{
            background: '#1e293b', color: '#94a3b8', border: 'none',
            borderRadius: 8,
            padding: isMobile ? '12px 16px' : '8px 16px',
            cursor: 'pointer', fontSize: isMobile ? 14 : 13,
            fontWeight: 500,
          }}>
          ออกจากระบบ
        </button>
      </div>

      {/* Summary — 2x2 grid บน mobile */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? 10 : 16,
        marginBottom: isMobile ? 18 : 28,
      }}>
        {[
          { label: 'Tenant ทั้งหมด', value: tenants.length, color: '#0ea5e9' },
          { label: 'Active', value: tenants.filter(t => t.status === 'active').length, color: '#22c55e' },
          { label: 'Suspended/Expired', value: tenants.filter(t => t.status !== 'active').length, color: '#ef4444' },
          { label: 'หมดอายุใน 7 วัน', value: tenants.filter(t => t.expires_at && new Date(t.expires_at) < new Date(Date.now() + 7 * 86400000)).length, color: '#f59e0b' },
        ].map((s, i) => (
          <div key={i} style={{
            background: '#1e293b',
            borderRadius: 12,
            padding: isMobile ? '14px 14px' : '18px 20px',
            borderLeft: `4px solid ${s.color}`,
            minWidth: 0,
          }}>
            <div style={{
              fontSize: isMobile ? 22 : 28,
              fontWeight: 700, color: s.color,
              lineHeight: 1.1,
            }}>{s.value}</div>
            <div style={{
              fontSize: isMobile ? 11 : 13,
              color: '#64748b', marginTop: 4,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: isMobile ? 0 : 4,
        marginBottom: isMobile ? 16 : 20,
        background: isMobile ? '#1e293b' : 'transparent',
        borderRadius: isMobile ? 10 : 0,
        padding: isMobile ? 4 : 0,
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
      }}>
        {/* sa_menu_tidy_v1 */[['tenants', '👥 Tenants'], ['pending', '⏳ รออนุมัติ'], ['payments', '💰 รออนุมัติชำระ'], ['banks', '💳 ข้อมูลชำระเงิน'], ['packages', '📦 แพ็กเกจสมาชิก'], ['ai', '🤖 AI/โทเคน'], ['referrals', '🎁 แนะนำเพื่อน'], ['billusers', '🧾 ผู้ใช้บิล'], ['brands', '🌐 โดเมน/แบรนด์'], ['gameproviders', '🎰 ค่ายเกม'], ['settings', '⚙️ ระบบ']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            style={{
              flex: isMobile ? 1 : 'none',
              padding: isMobile ? '10px 12px' : '9px 20px',
              borderRadius: 8, border: 'none',
              cursor: 'pointer',
              fontSize: isMobile ? 12 : 13,
              fontWeight: 600,
              background: activeTab === key ? '#0ea5e9' : (isMobile ? 'transparent' : '#1e293b'),
              color: activeTab === key ? '#fff' : '#64748b',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}>
            {label}
            {key === 'pending' && pending.length > 0 && (
              <span style={{
                background: '#ef4444', color: '#fff',
                borderRadius: 10, padding: '1px 7px',
                fontSize: 11, marginLeft: 6, fontWeight: 700,
              }}>{pending.length}</span>
            )}
            {key === 'payments' && payments.length > 0 && (
              <span style={{ marginLeft: 6, background: '#22c55e', color: '#fff', borderRadius: 999, padding: '1px 8px', fontSize: 10, fontWeight: 700 }}>{payments.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Pending Tab */}
      {activeTab === 'ai' && <SuperAdminAI />}
      {activeTab === 'referrals' && (
        /* referrals_tab_v1 */
        <ReferralsCard token={token} isMobile={isMobile} />
      )}

      {activeTab === 'banks' && (
        <BankAccountsCard token={token} isMobile={isMobile} />
      )}

      {activeTab === 'billusers' && (
        <SuperAdminBillUsers token={token} isMobile={isMobile} />
      )}

      {/* game_providers_v1 */}
      {activeTab === 'gameproviders' && (
        <GameProvidersPanel superToken={token} isMobile={isMobile} />
      )}

      {activeTab === 'brands' && (
        <div style={{ background:'#fff', borderRadius:12, padding:20, marginTop:16 }}>
          <h2 style={{ margin:'0 0 4px', fontSize:18, fontWeight:700, color:'#1e293b' }}>🌐 โดเมน & แบรนด์ (White-label)</h2>
          <p style={{ margin:'0 0 16px', fontSize:13, color:'#64748b' }}>ตั้งชื่อแบรนด์ต่อโดเมน — ผู้เข้าชมโดเมนนั้นจะเห็นชื่อแบรนด์นี้ทั้งระบบ (ฟังก์ชันเหมือนเดิมทุกอย่าง) · โดเมนใหม่ต้องชี้ DNS มาที่เซิร์ฟเวอร์ + ตั้ง SSL ก่อน (แจ้งผู้ดูแลระบบ)</p>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
            <input placeholder="โดเมน เช่น chat.mybrand.com" value={pbForm.domain} onChange={e=>setPbForm({...pbForm, domain:e.target.value})} style={{ flex:'1 1 240px', padding:'8px 12px', border:'1px solid #cbd5e1', borderRadius:8, fontSize:13 }} />
            <input placeholder="ชื่อแบรนด์ เช่น MyBrand Chat" value={pbForm.brand_name} onChange={e=>setPbForm({...pbForm, brand_name:e.target.value})} style={{ flex:'1 1 200px', padding:'8px 12px', border:'1px solid #cbd5e1', borderRadius:8, fontSize:13 }} />
            <button onClick={addPbrand} style={{ background:'#22c55e', color:'#fff', border:'none', borderRadius:8, padding:'8px 16px', fontWeight:700, cursor:'pointer' }}>+ เพิ่ม</button>
          </div>
          <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>
{/* brand_meta_v1 */}
              {['โดเมน','ชื่อแบรนด์','Title (แชร์ลิงก์/SEO)','Description','เปิด',''].map((h,i)=>(<th key={i} style={{ textAlign:i<4?'left':'center', padding:'8px', fontSize:12, color:'#94a3b8', borderBottom:'1px solid #e2e8f0', whiteSpace:'nowrap' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {pbrands.map((b,i)=>(
                <tr key={b.id}>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9' }}><input value={b.domain} onChange={e=>setPbField(i,'domain',e.target.value)} style={{ width:'100%', minWidth:180, padding:'6px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:13 }} /></td>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9' }}><input value={b.brand_name} onChange={e=>setPbField(i,'brand_name',e.target.value)} style={{ width:'100%', minWidth:140, padding:'6px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:13 }} /></td>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9' }}><input value={b.title||''} placeholder="(ว่าง = อัตโนมัติ)" onChange={e=>setPbField(i,'title',e.target.value)} style={{ width:'100%', minWidth:200, padding:'6px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:12 }} /></td>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9' }}><input value={b.description||''} placeholder="(ว่าง = อัตโนมัติ)" onChange={e=>setPbField(i,'description',e.target.value)} style={{ width:'100%', minWidth:240, padding:'6px 8px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:12 }} /></td>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9', textAlign:'center' }}><input type="checkbox" checked={!!b.is_active} onChange={e=>setPbField(i,'is_active',e.target.checked)} /></td>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9', textAlign:'center', whiteSpace:'nowrap' }}>
                    <button onClick={()=>savePbrand(b)} style={{ background:'#0ea5e9', color:'#fff', border:'none', borderRadius:6, padding:'6px 12px', marginRight:6, cursor:'pointer' }}>บันทึก</button>
                    <button onClick={()=>delPbrand(b.id)} style={{ background:'#ef4444', color:'#fff', border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer' }}>ลบ</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {/* alert_domain_v1: tenant -> alert-link domain */}
          <h3 style={{ margin:'26px 0 4px', fontSize:15, fontWeight:700, color:'#1e293b' }}>🔗 ลิงก์แจ้งเตือนของแต่ละร้าน</h3>
          <p style={{ margin:'0 0 12px', fontSize:12.5, color:'#64748b' }}>เลือกโดเมนที่ใช้ในลิงก์แจ้งเตือน Telegram ของแต่ละร้าน — ให้ตรงกับแบรนด์ที่แอดมินร้านนั้นใช้ (ไม่ตั้ง = ใช้ค่ากลาง)</p>
          <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr>{['ร้าน','โดเมนลิงก์แจ้งเตือน'].map((h,i)=>(<th key={i} style={{ textAlign:'left', padding:'8px', fontSize:12, color:'#94a3b8', borderBottom:'1px solid #e2e8f0' }}>{h}</th>))}</tr></thead>
            <tbody>
              {ptenants.map((t)=>(
                <tr key={t.id}>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9', fontSize:13, fontWeight:600, color:'#1e293b' }}>{t.name}</td>
                  <td style={{ padding:8, borderBottom:'1px solid #f1f5f9' }}>
                    <select value={t.app_domain || ''} onChange={e=>setTenantDomain(t.id, e.target.value)} style={{ padding:'6px 10px', border:'1px solid #cbd5e1', borderRadius:6, fontSize:13, minWidth:240 }}>
                      <option value="">— ใช้ค่าเริ่มต้น (chat.aiautochat.net) —</option>
                      {pbrands.map(b=>(<option key={b.id} value={b.domain}>{b.domain} ({b.brand_name})</option>))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
      {activeTab === 'settings' && (<>
        <SystemSettingsCard token={token} isMobile={isMobile} />
        <TwoFactorCard isMobile={isMobile} /> {/* superadmin_2fa_v1 */}
        <TokenHealthCard token={token} isMobile={isMobile} />
      </>)}

      {activeTab === 'pending' && (
        <div style={{
          background: '#1e293b',
          borderRadius: 14,
          overflow: 'hidden',
          marginBottom: 24,
        }}>
          <div style={{ padding: isMobile ? '14px 16px' : '16px 24px', borderBottom: '1px solid #0f172a' }}>
            <h2 style={{
              margin: 0,
              fontSize: isMobile ? 14 : 15,
              fontWeight: 600, color: '#f1f5f9',
            }}>รายการรออนุมัติ ({pending.length})</h2>
          </div>
          {pending.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>ไม่มีรายการรออนุมัติ</div>
          ) : isMobile ? (
            /* mobile: Card list */
            <div>
              {pending.map((t, i) => (
                <PendingCard key={t.id} tenant={t}
                  onApprove={handleApprove} onReject={handleReject}
                  isLast={i === pending.length - 1} />
              ))}
            </div>
          ) : (
            /* desktop: ตารางเดิม */
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  {['ชื่อธุรกิจ', 'อีเมล', 'แพ็คเกจ', 'ราคา', 'ข้อมูลติดต่อ', 'วันที่สมัคร', ''].map((h, i) => (
                    <th key={i} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pending.map(t => (
                  <tr key={t.id} style={{ borderTop: '1px solid #0f172a' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#263548'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '14px 16px', color: '#f1f5f9', fontWeight: 600 }}>{t.name}</td>
                    <td style={{ padding: '14px 16px', color: '#94a3b8', fontSize: 13 }}>{t.email}</td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: '#0ea5e920', color: '#0ea5e9', borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>
                        {t.package_name || 'ไม่ระบุ'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', color: '#22c55e', fontWeight: 600, fontSize: 14 }}>
                      {t.price > 0 ? `฿${parseInt(t.price).toLocaleString()}` : 'ฟรี'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: '#64748b' }}>{t.note || '-'}</td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: '#64748b' }}>
                      {new Date(t.created_at).toLocaleDateString('th-TH')}
                    </td>
                    <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleApprove(t.id)}
                        style={{ background: '#22c55e20', color: '#22c55e', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, marginRight: 6 }}>
                        ✓ อนุมัติ
                      </button>
                      <button onClick={() => handleReject(t.id)}
                        style={{ background: '#ef444420', color: '#ef4444', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                        ✗ ปฏิเสธ
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* payments_v1: Payment Requests Tab */}
      {activeTab === "payments" && (
        <div style={{ background: "#1e293b", borderRadius: 14, overflow: "hidden", marginBottom: 24 }}>
          <div style={{ padding: isMobile ? "14px 16px" : "16px 24px", borderBottom: "1px solid #0f172a" }}>
            <h2 style={{ margin: 0, fontSize: isMobile ? 14 : 15, fontWeight: 600, color: "#f1f5f9" }}>
              💰 รออนุมัติชำระเงิน ({payments.length})
            </h2>
          </div>
          {payments.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#475569" }}>ไม่มีรายการรออนุมัติ</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#0f172a" }}>
                    {["ร้าน", "อีเมล", "แพ็กเกจ", "ยอด", "สลิป", "วันที่ส่ง", ""].map((h, i) => (
                      <th key={i} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "#475569", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} style={{ borderTop: "1px solid #0f172a" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#263548"}
                      onMouseLeave={e => e.currentTarget.style.background = ""}>
                      <td style={{ padding: "14px 16px", color: "#f1f5f9", fontWeight: 600 }}>{p.tenant_name}</td>
                      <td style={{ padding: "14px 16px", color: "#94a3b8", fontSize: 13 }}>{p.tenant_email}</td>
                      <td style={{ padding: "14px 16px" }}>
                        <span style={{ background: "#0ea5e920", color: "#0ea5e9", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
                          {(p.package_name || "").toUpperCase()}
                        </span>
                      </td>
                      <td style={{ padding: "14px 16px", color: "#22c55e", fontWeight: 700, fontSize: 14 }}>
                        ฿{Number(p.amount).toLocaleString()}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        {p.slip_url ? (
                          <a href={p.slip_url} target="_blank" rel="noreferrer"
                            style={{ color: "#0ea5e9", textDecoration: "none", fontSize: 12, fontWeight: 600 }}>
                            📎 ดูสลิป
                          </a>
                        ) : <span style={{ color: "#64748b", fontSize: 12 }}>-</span>}
                      </td>
                      <td style={{ padding: "14px 16px", fontSize: 12, color: "#64748b" }}>
                        {new Date(p.created_at).toLocaleDateString("th-TH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                        <button onClick={() => handleApprovePayment(p.id)}
                          style={{ background: "#22c55e20", color: "#22c55e", border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontWeight: 600, fontSize: 12, marginRight: 6 }}>
                          ✓ อนุมัติ
                        </button>
                        <button onClick={() => handleRejectPayment(p.id)}
                          style={{ background: "#ef444420", color: "#ef4444", border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
                          ✗ ปฏิเสธ
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

            {/* Packages Tab */}
      {activeTab === 'packages' && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            marginBottom: 16,
          }}>
            <button onClick={() => { setEditPkg(null); setPkgForm(emptyPkg); setShowPkgForm(true); }}
              style={{
                width: isMobile ? '100%' : 'auto',
                background: '#0ea5e9', color: '#fff', border: 'none',
                borderRadius: 8,
                padding: isMobile ? '12px 20px' : '10px 20px',
                cursor: 'pointer', fontWeight: 600,
                fontSize: isMobile ? 14 : 13,
              }}>
              + เพิ่มแพ็คเกจ
            </button>
          </div>

          {showPkgForm && (
            <div style={{
              background: '#1e293b', borderRadius: 14,
              padding: isMobile ? 18 : 28,
              marginBottom: 20,
            }}>
              <h3 style={{
                color: '#f1f5f9',
                margin: '0 0 16px',
                fontSize: isMobile ? 14 : 15,
              }}>{editPkg ? 'แก้ไขแพ็คเกจ' : 'เพิ่มแพ็คเกจใหม่'}</h3>

              {/* Name + Price + Duration — 1 col บน mobile */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                gap: isMobile ? 12 : 14,
                marginBottom: isMobile ? 12 : 14,
              }}>
                <div>
                  <DarkLabel>ชื่อแพ็คเกจ</DarkLabel>
                  <input value={pkgForm.name} onChange={e => setPkgForm({ ...pkgForm, name: e.target.value })}
                    style={darkInputStyle(isMobile)} />
                </div>
                <div>
                  <DarkLabel>ราคา (บาท/เดือน)</DarkLabel>
                  <input type="number" value={pkgForm.price}
                    onChange={e => setPkgForm({ ...pkgForm, price: parseFloat(e.target.value) })}
                    style={darkInputStyle(isMobile)} />
                </div>
                <div>
                  <DarkLabel>ระยะเวลา (วัน)</DarkLabel>
                  <input type="number" value={pkgForm.duration_days}
                    onChange={e => setPkgForm({ ...pkgForm, duration_days: parseInt(e.target.value) })}
                    style={darkInputStyle(isMobile)} />
                </div>
              </div>

              {/* Limits — 2x2 บน mobile */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
                gap: isMobile ? 10 : 14,
                marginBottom: isMobile ? 12 : 14,
              }}>
                {[['max_channels', 'Social Accounts'], ['max_agents', 'จำนวน Admin'], ['max_kb', 'จำนวน KB'], ['slip_quota_monthly', '💳 Slip Quota'], ['max_telegrams', '📲 Telegrams'], ['sort_order', 'ลำดับ']].map(([k, l]) => (
                  <div key={k}>
                    <DarkLabel>{l}</DarkLabel>
                    <input type="number" value={pkgForm[k]}
                      onChange={e => setPkgForm({ ...pkgForm, [k]: parseInt(e.target.value) })}
                      style={darkInputStyle(isMobile)} />
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: isMobile ? 12 : 14 }}>
                <DarkLabel>คำอธิบาย</DarkLabel>
                <input value={pkgForm.description}
                  onChange={e => setPkgForm({ ...pkgForm, description: e.target.value })}
                  style={darkInputStyle(isMobile)} />
              </div>

              {/* Features — mobile_v2: clickable cards เพื่อให้ checkbox เด่นชัดบน mobile */}
              <div style={{ marginBottom: 20 }}>
                <DarkLabel>Features</DarkLabel>
                <div style={{
                  display: isMobile ? 'grid' : 'flex',
                  gridTemplateColumns: isMobile ? '1fr 1fr' : 'none',
                  flexDirection: isMobile ? 'initial' : 'row',
                  gap: isMobile ? 8 : 20,
                  marginTop: 6,
                }}>
                  {[
                    ['bot_training', '🤖 AI Training (เทรนบอท)'],
                    ['telegram_notify', '📲 แจ้งเตือน Telegram'],
                    ['kpi_dashboard', '📊 KPI Dashboard'],
                    ['quick_replies', '⚡ Quick Replies'],
                    /* pkg_feature_gates_v1 */
                    ['chat', '💬 ระบบแชท'],
                    ['bill', '🧾 ระบบบิล'],
                    ['finance', '💰 ระบบการเงิน'],
                  ].map(([k, label]) => {
                    const checked = !!(pkgForm.features && pkgForm.features[k]);
                    return (
                      <label key={k} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        color: isMobile ? (checked ? '#0ea5e9' : '#94a3b8') : '#94a3b8',
                        fontSize: isMobile ? 13 : 13,
                        cursor: 'pointer',
                        padding: isMobile ? '12px 12px' : 0,
                        borderRadius: isMobile ? 8 : 0,
                        border: isMobile ? `2px solid ${checked ? '#0ea5e9' : '#334155'}` : 'none',
                        background: isMobile ? (checked ? '#0ea5e920' : '#0f172a') : 'transparent',
                        transition: 'all 0.15s',
                        fontWeight: isMobile && checked ? 600 : 400,
                      }}>
                        <input type="checkbox" checked={checked}
                          onChange={e => setPkgForm({ ...pkgForm, features: { ...pkgForm.features, [k]: e.target.checked } })}
                          style={{
                            width: isMobile ? 18 : 16,
                            height: isMobile ? 18 : 16,
                            accentColor: '#0ea5e9',
                            flexShrink: 0,
                            cursor: 'pointer',
                          }} />
                        <span style={{ flex: 1 }}>{label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: 10,
                justifyContent: isMobile ? 'stretch' : 'flex-end',
                flexDirection: isMobile ? 'column' : 'row',
              }}>
                <button onClick={() => { setShowPkgForm(false); setEditPkg(null); }}
                  style={{
                    padding: isMobile ? '12px 20px' : '9px 20px',
                    borderRadius: 8, background: '#334155', color: '#94a3b8',
                    border: 'none', cursor: 'pointer',
                    fontSize: 14, fontWeight: 500,
                    order: isMobile ? 2 : 1,
                  }}>ยกเลิก</button>
                <button onClick={handleSavePkg}
                  style={{
                    padding: isMobile ? '12px 24px' : '9px 24px',
                    borderRadius: 8, background: '#0ea5e9', color: '#fff',
                    border: 'none', cursor: 'pointer',
                    fontSize: 14, fontWeight: 600,
                    order: isMobile ? 1 : 2,
                  }}>บันทึก</button>
              </div>
            </div>
          )}

          {/* Package cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: isMobile ? 10 : 16,
          }}>
            {packages.map(pkg => (
              <div key={pkg.id} style={{
                background: '#1e293b', borderRadius: 12,
                padding: isMobile ? 16 : 20,
                border: '1px solid #334155',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <h3 style={{ margin: 0, color: '#f1f5f9', fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{pkg.name}</h3>
                  <span style={{ color: '#22c55e', fontWeight: 700, marginLeft: 8, flexShrink: 0 }}>{pkg.price > 0 ? `฿${parseInt(pkg.price).toLocaleString()}` : 'ฟรี'}</span>
                </div>
                <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>{pkg.description}</p>
                <div style={{ fontSize: 12, color: '#94a3b8', display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
                  <span>📡 Social: {pkg.max_channels} | 👤 Admin: {pkg.max_agents} | 🤖 KB: {pkg.max_kb} | 💳 Slips: {pkg.slip_quota_monthly || 50} | 📲 TG: {pkg.max_telegrams || 1}</span>
                  <span>📅 {pkg.duration_days} วัน | ลำดับ: {pkg.sort_order}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setEditPkg(pkg); setPkgForm({ ...pkg, features: typeof pkg.features === 'string' ? JSON.parse(pkg.features) : pkg.features }); setShowPkgForm(true); }}
                    style={{
                      flex: 1,
                      padding: isMobile ? '10px' : '7px',
                      borderRadius: 6,
                      background: '#0ea5e920', color: '#0ea5e9',
                      border: 'none', cursor: 'pointer',
                      fontSize: isMobile ? 13 : 12, fontWeight: 500,
                    }}>แก้ไข</button>
                  <button onClick={() => handleDeletePkg(pkg.id)}
                    style={{
                      flex: 1,
                      padding: isMobile ? '10px' : '7px',
                      borderRadius: 6,
                      background: '#ef444420', color: '#ef4444',
                      border: 'none', cursor: 'pointer',
                      fontSize: isMobile ? 13 : 12, fontWeight: 500,
                    }}>ลบ</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tenants Tab */}
      {activeTab === 'tenants' && (
        <div style={{ background: '#1e293b', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: isMobile ? '14px 16px' : '16px 24px', borderBottom: '1px solid #0f172a' }}>
            <h2 style={{
              margin: 0,
              fontSize: isMobile ? 14 : 15,
              fontWeight: 600, color: '#f1f5f9',
            }}>รายชื่อ Tenant ({tenants.length})</h2>
          </div>

          {tenants.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>ยังไม่มี Tenant</div>
          ) : isMobile ? (
            /* mobile: Card list */
            <div>
              {sortedTenants.map((t, i) => (
                <TenantCard onQuota={setQuotaTenant} key={t.id} tenant={t}
                  statusColor={statusColor} planColor={planColor}
                  onEdit={setEditTenant} onToggle={handleToggle} onRenew={handleRenew}
                  isLast={i === tenants.length - 1} />
              ))}
            </div>
          ) : (
            /* desktop: ตารางเดิม */
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0f172a' }}>
                  {[
                    { label: 'Tenant', field: null },
                    { label: 'Plan', field: 'plan' },
                    { label: 'Status', field: 'status' },
                    { label: 'หมดอายุ', field: 'expires_at' },
                    { label: 'Social', field: null },
                    { label: 'Admin', field: null },
                    { label: 'KB', field: null },
                    { label: 'Quota', field: null },
                    /* label_fix_v1: ช่องนี้แสดง t.note มาตลอด ไม่ใช่ตัวเลขการใช้งาน
                       หัวตารางเดิมเขียนว่า "การใช้งาน" ทำให้เข้าใจผิดว่าจะเห็นสถิติ */
                    { label: 'โน้ต', field: null },
                    { label: '', field: null },
                  ].map((col, i) => {
                    const sortable = !!col.field;
                    const active = sortField === col.field;
                    const icon = !sortable ? '' : (active ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ' ↕');
                    return (
                      <th key={i}
                        onClick={sortable ? () => handleSort(col.field) : undefined}
                        style={{
                          padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600,
                          color: active ? '#38bdf8' : '#475569', textTransform: 'uppercase',
                          cursor: sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                        title={sortable ? 'คลิกเพื่อจัดเรียง' : undefined}
                      >{col.label}{icon}</th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedTenants.map(t => (
                  <tr key={t.id} style={{ borderTop: '1px solid #0f172a' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#263548'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 600, color: t.is_active ? '#f1f5f9' : '#475569', fontSize: 14 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: '#475569' }}>{t.email}</div>
                      {!t.is_active && <span style={{ fontSize: 10, background: '#ef4444', color: '#fff', borderRadius: 4, padding: '1px 6px' }}>DISABLED</span>}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: planColor(t.plan) + '22', color: planColor(t.plan), borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                        {t.plan || '-'}
                      </span>
                      {/* combined_view_v1: ราคารวม แชท+บิล */}
                      {t.has_bill_feature && (() => {
                        const cp = t.has_chat_feature ? (parseFloat(t.chat_price) || 0) : 0;
                        const bp = parseFloat(t.bill_price) || 0;
                        if (!cp && !bp) return null;
                        return <div style={{ fontSize: 10, color: '#64748b', marginTop: 4, whiteSpace: 'nowrap' }}>
                          💬฿{cp.toLocaleString()} + 🧾฿{bp.toLocaleString()} = <b style={{ color: '#22c55e' }}>฿{(cp + bp).toLocaleString()}</b>
                        </div>;
                      })()}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <span style={{ background: statusColor(t.status) + '22', color: statusColor(t.status), borderRadius: 6, padding: '3px 10px', fontSize: 12 }}>
                        {t.status || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {/* combined_view_v1: วันหมดอายุ แชท + บิล */}
                      <div style={{ color: t.expires_at && new Date(t.expires_at) < new Date() ? '#ef4444' : '#94a3b8' }}>
                        💬 {t.expires_at ? new Date(t.expires_at).toLocaleDateString('th-TH') : '-'}
                      </div>
                      {t.has_bill_feature && (
                        <div style={{ marginTop: 3, color: t.bill_expires_at ? (new Date(t.bill_expires_at) < new Date() ? '#ef4444' : '#94a3b8') : '#f59e0b' }}>
                          🧾 {t.bill_expires_at ? new Date(t.bill_expires_at).toLocaleDateString('th-TH') : 'ยังไม่ตั้ง'}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {(() => { /* quota_color_threshold_v1 */
                        const used = parseInt(t.channel_count) || 0;
                        const max = t.max_channels || 0;
                        const pct = max > 0 ? (used / max) * 100 : 0;
                        const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';
                        return <span style={{ color, fontWeight: 600 }}>{t.channel_count}/{t.max_channels || '∞'}</span>;
                      })()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {(() => { /* quota_color_threshold_v1 */
                        const used = parseInt(t.agent_count) || 0;
                        const max = t.max_agents || 0;
                        const pct = max > 0 ? (used / max) * 100 : 0;
                        const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';
                        return <span style={{ color, fontWeight: 600 }}>{t.agent_count}/{t.max_agents || '∞'}</span>;
                      })()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {(() => { /* quota_color_threshold_v1 */
                        const used = parseInt(t.kb_count) || 0;
                        const max = t.max_kb || 0;
                        const pct = max > 0 ? (used / max) * 100 : 0;
                        const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';
                        return <span style={{ color, fontWeight: 600 }}>{t.kb_count}/{t.max_kb || '∞'}</span>;
                      })()}
                    </td>
                    {/* slip_quota_admin_v1: Quota column */}
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {(() => {
                        const used = t.slip_quota_used || 0;
                        const total = (t.slip_quota_monthly || 0) + (t.slip_quota_topup || 0);
                        const pct = total > 0 ? (used / total) * 100 : 0;
                        const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';
                        return (
                          <div>
                            <div style={{ color, fontWeight: 600 }}>{used}/{total}</div>
                            {t.slip_quota_topup > 0 && <div style={{ fontSize: 10, color: '#22c55e' }}>+{t.slip_quota_topup} top-up</div>}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: '#64748b', maxWidth: 150 }}>
                      {t.note || '-'}
                    </td>
                    <td style={{ padding: '14px 16px', whiteSpace: 'nowrap' }}>
                      <button onClick={() => setEditTenant(t)} title="ตั้งค่า License"
                        style={{ background: '#0ea5e920', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', marginRight: 6 }}>
                        <Edit2 size={13} color="#0ea5e9" />
                      </button>
                      {/* slip_quota_admin_v1: Quota button */}
                      <button onClick={() => setQuotaTenant(t)} title="จัดการเครดิตตรวจสลิป"
                        style={{ background: '#22c55e20', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', marginRight: 6 }}>
                        <CreditCard size={13} color="#22c55e" />
                      </button>
                      <button onClick={() => handleToggle(t.id)} title={t.is_active ? 'ปิดการใช้งาน' : 'เปิดการใช้งาน'}
                        style={{ background: t.is_active ? '#ef444420' : '#22c55e20', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>
                        <Power size={13} color={t.is_active ? '#ef4444' : '#22c55e'} />
                      </button>
                      <button onClick={() => handleRenew(t, 30)} title={t.has_bill_feature ? 'ต่ออายุแชท+บิล 30 วัน' : 'ต่ออายุ 30 วัน'}
                        style={{ background: '#22c55e20', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', marginLeft: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#22c55e' }}>+30วัน{t.has_bill_feature ? ' 🧾' : ''}</span>
                      </button>
                      {/* delete_tenant_v1: ปุ่มลบ tenant — v2: ใช้ is_active หรือ license status (= field 'status') */}
                      {(t.is_active === false || t.status === 'expired' || !t.status) && (
                        <button onClick={() => setDeleteTenant(t)} title="ลบ tenant (ถาวร)"
                          style={{ background: '#ef444420', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', marginLeft: 4 }}>
                          <Trash2 size={13} color="#ef4444" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editTenant && <LicenseModal tenant={editTenant} onSave={handleSaveLicense}
        onClose={() => setEditTenant(null)}
        onSaveProfile={handleSaveProfile} onResetPwd={handleResetPwd}
        packages={packages}
        allTenants={tenants} /* tenant_referrer_v2 */
      />}

      {/* delete_tenant_v1: Delete Confirmation Modal */}
      {deleteTenant && <DeleteTenantModal tenant={deleteTenant}
        onClose={() => setDeleteTenant(null)}
        onConfirm={handleDeleteTenant} />}

      {/* slip_quota_admin_v1: Quota Modal */}
      {quotaTenant && <QuotaModal tenant={quotaTenant} token={token}
        onClose={() => setQuotaTenant(null)}
        onSaved={() => { loadTenants(token); }} />}
    </div>
  );
}

// delete_tenant_v1: Modal ยืนยันการลบ — ต้องพิมพ์ชื่อ tenant ตรง
function DeleteTenantModal({ tenant, onClose, onConfirm }) {
  const [confirmName, setConfirmName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const isValid = confirmName.trim() === tenant.name;

  const submit = async () => {
    if (!isValid || busy) return;
    setBusy(true);
    try { await onConfirm(tenant, confirmName.trim()); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, padding: 24, maxWidth: 480, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#ef444420', padding: 10, borderRadius: '50%' }}>
            <Trash2 size={24} color="#ef4444" />
          </div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>
            ลบ Tenant ถาวร
          </h3>
        </div>

        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
          padding: 12, marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          ⚠️ <strong>การลบนี้ถาวร!</strong> จะลบข้อมูลทั้งหมด: agents, channels, conversations, messages, customers, kb, file uploads และอื่นๆ
          <br /><br />
          ✅ ระบบจะสำรองข้อมูลไว้อัตโนมัติที่ <code>/backup/tenant-deletions/</code> ก่อนลบ
        </div>

        <div style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Tenant:</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#1e293b' }}>{tenant.name}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{tenant.email}</div>
        </div>

        <label style={{ fontSize: 13, color: '#475569', marginBottom: 6, display: 'block', fontWeight: 600 }}>
          พิมพ์ชื่อ <code style={{ background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>{tenant.name}</code> เพื่อยืนยัน:
        </label>
        <input
          type="text"
          value={confirmName}
          onChange={e => setConfirmName(e.target.value)}
          placeholder={`พิมพ์ "${tenant.name}" ตรงนี้`}
          autoFocus
          disabled={busy}
          style={{
            width: '100%', padding: '10px 12px', fontSize: 14,
            border: `2px solid ${isValid ? '#22c55e' : '#e2e8f0'}`,
            borderRadius: 8, outline: 'none', boxSizing: 'border-box',
            background: busy ? '#f1f5f9' : '#fff'
          }}
        />

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy}
            style={{ padding: '10px 18px', border: '1px solid #e2e8f0',
              background: '#fff', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
              fontSize: 13, color: '#475569', fontWeight: 600 }}>
            ยกเลิก
          </button>
          <button onClick={submit} disabled={!isValid || busy}
            style={{ padding: '10px 18px', border: 'none',
              background: isValid && !busy ? '#ef4444' : '#fca5a5',
              color: '#fff', borderRadius: 8, cursor: (isValid && !busy) ? 'pointer' : 'not-allowed',
              fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Trash2 size={14} /> {busy ? 'กำลังลบ...' : 'ลบถาวร'}
          </button>
        </div>
      </div>
    </div>
  );
}

// mobile_v1: Tenant Card สำหรับ mobile
// slip_quota_admin_v1: Quota Management Modal
function QuotaModal({ tenant, token, onClose, onSaved }) {
  const [topupAmt, setTopupAmt] = React.useState('');
  const [topupMode, setTopupMode] = React.useState('add'); // 'add' | 'set' — slip_quota_admin_v2
  const [monthlyAmt, setMonthlyAmt] = React.useState(tenant.slip_quota_monthly || 50);
  const [topupNote, setTopupNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const used = tenant.slip_quota_used || 0;
  const monthly = tenant.slip_quota_monthly || 0;
  const topup = tenant.slip_quota_topup || 0;
  const total = monthly + topup;
  const remaining = Math.max(0, total - used);
  const percent = total > 0 ? Math.round((used / total) * 100) : 0;

  const doTopup = async () => {
    const amt = parseInt(topupAmt);
    if (isNaN(amt) || amt < 0) { toast.error('กรุณากรอกจำนวนให้ถูกต้อง'); return; }
    if (topupMode === 'add' && amt < 1) { toast.error('จำนวนต้องมากกว่า 0'); return; }
    setBusy(true);
    try {
      if (topupMode === 'add') {
        await SA.post(`/api/superadmin/tenants/${tenant.id}/quota/topup`,
          { amount: amt, note: topupNote || null },
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`✅ เติม +${amt} เครดิตให้ ${tenant.name}`);
      } else {
        await SA.put(`/api/superadmin/tenants/${tenant.id}/quota/topup-set`,
          { topup: amt, note: topupNote || null },
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success(`✅ ตั้งค่า Top-up = ${amt} ของ ${tenant.name}`);
      }
      setTopupAmt(''); setTopupNote('');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setBusy(false);
  };

  const doMonthly = async () => {
    const amt = parseInt(monthlyAmt);
    if (amt < 0) { toast.error('จำนวนต้องไม่ติดลบ'); return; }
    if (amt === monthly) { toast.error('จำนวนไม่เปลี่ยนแปลง'); return; }
    setBusy(true);
    try {
      await SA.put(`/api/superadmin/tenants/${tenant.id}/quota/monthly`,
        { monthly: amt },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`✅ เปลี่ยน Monthly เป็น ${amt}/เดือน`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setBusy(false);
  };

  const doReset = async () => {
    if (!window.confirm(`รีเซ็ต Used ของ ${tenant.name} เป็น 0?`)) return;
    setBusy(true);
    try {
      await SA.post(`/api/superadmin/tenants/${tenant.id}/quota/reset`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`✅ รีเซ็ต Used = 0`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setBusy(false);
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#1e293b', borderRadius: 14, padding: 24, maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto', color: '#f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <CreditCard size={22} color="#22c55e" />
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>จัดการเครดิต: {tenant.name}</h2>
        </div>

        {/* Current usage */}
        <div style={{ background: '#0f172a', padding: 16, borderRadius: 10, marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>การใช้งานปัจจุบัน</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: percent >= 100 ? '#ef4444' : percent >= 80 ? '#f59e0b' : '#22c55e' }}>
              {used}/{total}
            </span>
          </div>
          <div style={{ background: '#334155', height: 8, borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
            <div style={{ width: `${Math.min(100, percent)}%`, height: '100%', background: percent >= 100 ? '#ef4444' : percent >= 80 ? '#f59e0b' : '#22c55e' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, fontSize: 12 }}>
            <div style={{ background: '#1e293b', padding: '6px 10px', borderRadius: 6 }}>
              <div style={{ color: '#64748b', fontSize: 10 }}>Monthly</div>
              <div style={{ color: '#f1f5f9', fontWeight: 600 }}>{monthly}</div>
            </div>
            <div style={{ background: '#1e293b', padding: '6px 10px', borderRadius: 6 }}>
              <div style={{ color: '#64748b', fontSize: 10 }}>Top-up</div>
              <div style={{ color: '#22c55e', fontWeight: 600 }}>+{topup}</div>
            </div>
            <div style={{ background: '#1e293b', padding: '6px 10px', borderRadius: 6 }}>
              <div style={{ color: '#64748b', fontSize: 10 }}>คงเหลือ</div>
              <div style={{ color: '#3b82f6', fontWeight: 700 }}>{remaining}</div>
            </div>
          </div>
        </div>

        {/* Section 1: Top-up */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8, fontWeight: 600 }}>💳 จัดการ Top-up (ไม่หมดอายุ)</h3>

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, background: '#0f172a', padding: 4, borderRadius: 8, border: '1px solid #334155' }}>
            <button onClick={() => setTopupMode('add')}
              style={{ flex: 1, padding: '7px 10px', borderRadius: 6, background: topupMode === 'add' ? '#22c55e' : 'transparent', color: topupMode === 'add' ? '#fff' : '#64748b', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              ➕ เพิ่ม
            </button>
            <button onClick={() => setTopupMode('set')}
              style={{ flex: 1, padding: '7px 10px', borderRadius: 6, background: topupMode === 'set' ? '#0ea5e9' : 'transparent', color: topupMode === 'set' ? '#fff' : '#64748b', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              🎯 ตั้งค่าใหม่
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input type="number" min="0" max="100000" value={topupAmt}
              onChange={e => setTopupAmt(e.target.value)}
              placeholder={topupMode === 'add' ? `เช่น 100 (จะเป็น ${(tenant.slip_quota_topup || 0) + 100})` : `ตั้งเป็น... (ปัจจุบัน ${tenant.slip_quota_topup || 0})`}
              style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 14, outline: 'none' }} />
            <button onClick={doTopup} disabled={busy || topupAmt === ''}
              style={{ padding: '9px 16px', borderRadius: 8, background: topupMode === 'add' ? '#22c55e' : '#0ea5e9', color: '#fff', border: 'none', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, fontSize: 13, whiteSpace: 'nowrap' }}>
              {topupMode === 'add' ? '+ เติม' : 'บันทึก'}
            </button>
          </div>
          <input type="text" value={topupNote} onChange={e => setTopupNote(e.target.value)} placeholder="หมายเหตุ (optional)"
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />

          {topupMode === 'set' && (
            <div style={{ marginTop: 8, padding: '8px 12px', background: '#0c4a6e22', borderLeft: '3px solid #0ea5e9', borderRadius: 6, fontSize: 11, color: '#7dd3fc' }}>
              💡 ตั้ง Top-up เป็นจำนวนที่กรอก (แทนที่ค่าเดิม) — ใช้ตอนกรอกผิดเติมเกิน
            </div>
          )}
        </div>

        {/* Section 2: Monthly */}
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 13, color: '#94a3b8', marginBottom: 8, fontWeight: 600 }}>📅 เปลี่ยน Monthly Quota</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="number" min="0" max="100000" value={monthlyAmt}
              onChange={e => setMonthlyAmt(e.target.value)}
              style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 14, outline: 'none' }} />
            <span style={{ fontSize: 12, color: '#64748b' }}>/เดือน</span>
            <button onClick={doMonthly} disabled={busy}
              style={{ padding: '9px 16px', borderRadius: 8, background: '#0ea5e9', color: '#fff', border: 'none', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, fontSize: 13 }}>
              บันทึก
            </button>
          </div>
        </div>

        {/* Section 3: Reset */}
        <div style={{ marginBottom: 18, padding: 12, background: '#0f172a', borderRadius: 8, border: '1px solid #334155' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, color: '#f1f5f9', fontWeight: 600 }}>🔄 รีเซ็ต Used</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>ตั้ง used = 0 (ไม่กระทบ topup)</div>
            </div>
            <button onClick={doReset} disabled={busy}
              style={{ padding: '8px 14px', borderRadius: 8, background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, fontSize: 12 }}>
              รีเซ็ต
            </button>
          </div>
        </div>

        <button onClick={onClose}
          style={{ width: '100%', padding: 10, borderRadius: 8, background: 'transparent', color: '#94a3b8', border: '1px solid #334155', cursor: 'pointer', fontSize: 14 }}>
          ปิด
        </button>
      </div>
    </div>
  );
}

// global_token_v1: Slip2Go Global Token Management Card
// bank_accounts_v1: SuperAdmin tab for managing payment bank accounts
// Pattern matches SystemSettingsCard / LicenseModal in SuperAdmin.js

// admin_credit_adjust_ui_v1: Credit Adjust Modal component
function CreditAdjustModal({ tenant, token, onClose, onSaved }) {
  const [amount, setAmount] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const currentCredit = parseInt(tenant.referral_credit || 0, 10);

  const quickSet = (delta) => setAmount(String(delta));

  const handleSubmit = async () => {
    const amt = parseInt(amount, 10);
    if (isNaN(amt) || amt === 0) {
      toast.error('กรุณากรอกจำนวนที่ไม่ใช่ 0');
      return;
    }
    if (!note.trim()) {
      toast.error('กรุณากรอกหมายเหตุ');
      return;
    }
    const newBalance = Math.max(0, currentCredit + amt);
    const confirmMsg = 'ยืนยันการปรับ Credit ของ ' + tenant.name + '?\n' +
      'Credit ปัจจุบัน: ' + currentCredit + ' ฿\n' +
      (amt > 0 ? '+' : '') + amt + ' ฿\n' +
      'Credit ใหม่: ' + newBalance + ' ฿';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await axios.post('/api/superadmin/credit-adjust',
        { tenant_id: tenant.id, amount: amt, note: note.trim() },
        { headers: { Authorization: 'Bearer ' + token } });
      toast.success('✅ ปรับ Credit สำเร็จ (' + (amt > 0 ? '+' : '') + amt + ' ฿)');
      onSaved();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.error) || 'เกิดข้อผิดพลาด');
    }
    setBusy(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#1e293b', borderRadius: 12,
        padding: 24, width: '100%', maxWidth: 480,
        border: '1px solid #334155'
      }}>
        <h3 style={{ margin: 0, marginBottom: 16, color: '#f59e0b' }}>
          💰 ปรับ Credit ของ {tenant.name}
        </h3>

        <div style={{
          background: '#0f172a', padding: 12, borderRadius: 8,
          marginBottom: 16, border: '1px solid #334155'
        }}>
          <div style={{ color: '#94a3b8', fontSize: 12 }}>Credit ปัจจุบัน</div>
          <div style={{ color: '#f59e0b', fontSize: 24, fontWeight: 700 }}>
            {currentCredit.toLocaleString()} ฿
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>
            จำนวน (+/- ก็ได้)
          </label>
          <input type="number" value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="เช่น 500 หรือ -200"
            style={{
              width: '100%', marginTop: 4, padding: 10,
              background: '#0f172a', color: '#e2e8f0',
              border: '1px solid #334155', borderRadius: 8, fontSize: 14
            }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {[100, 500, 1000, -100, -500].map(v => (
              <button key={v} onClick={() => quickSet(v)} type="button" style={{
                padding: '4px 10px', borderRadius: 6,
                background: v > 0 ? '#22c55e' : '#ef4444',
                color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12
              }}>{v > 0 ? '+' : ''}{v}</button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600 }}>
            หมายเหตุ <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input type="text" value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="เช่น: โบนัส marketing campaign"
            style={{
              width: '100%', marginTop: 4, padding: 10,
              background: '#0f172a', color: '#e2e8f0',
              border: '1px solid #334155', borderRadius: 8, fontSize: 14
            }} />
        </div>

        <div style={{
          fontSize: 11, color: '#64748b',
          padding: 8, background: '#0f172a', borderRadius: 6,
          marginBottom: 16
        }}>
          ℹ️ จะถูกบันทึกใน credit_transactions (audit log)
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={{
            padding: '10px 16px', borderRadius: 8,
            background: 'transparent', color: '#94a3b8',
            border: '1px solid #334155', cursor: 'pointer', fontSize: 14
          }}>ยกเลิก</button>
          <button onClick={handleSubmit} disabled={busy} style={{
            padding: '10px 20px', borderRadius: 8,
            background: busy ? '#475569' : '#f59e0b',
            color: '#fff', border: 'none',
            cursor: busy ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600
          }}>{busy ? 'กำลังบันทึก...' : '✅ บันทึก'}</button>
        </div>
      </div>
    </div>
  );
}

// referrals_tab_v1
function ReferralsCard({ token: superToken, isMobile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPercent, setSavingPercent] = useState(false);
  const [newPercent, setNewPercent] = useState('');
  /* referral_duration_v1: global default duration */
  const [newDuration, setNewDuration] = useState('4');
  const [savingDuration, setSavingDuration] = useState(false);
  /* admin_credit_adjust_ui_v1: Credit Adjust Modal state */
  const [creditAdjustTenant, setCreditAdjustTenant] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get('/api/superadmin/referrals', {
      headers: { Authorization: 'Bearer ' + superToken }
    })
      .then(({ data }) => {
        setData(data);
        setNewPercent(String(data.settings.percent));
        if (data.settings.duration_months != null) setNewDuration(String(data.settings.duration_months));
      })
      .catch(() => toast.error('โหลดข้อมูลไม่สำเร็จ'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const savePercent = async () => {
    const n = parseInt(newPercent, 10);
    if (isNaN(n) || n < 0 || n > 100) {
      toast.error('% ต้องอยู่ระหว่าง 0-100');
      return;
    }
    setSavingPercent(true);
    try {
      await axios.patch('/api/superadmin/settings/referral_percent',
        { value: String(n) },
        { headers: { Authorization: 'Bearer ' + superToken } }
      );
      toast.success('บันทึก ' + n + '% แล้ว');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'บันทึกไม่สำเร็จ');
    } finally {
      setSavingPercent(false);
    }
  };

  /* referral_duration_v1: save global default duration */
  const saveDuration = async () => {
    const n = parseInt(newDuration, 10);
    if (isNaN(n) || n < 0 || n > 60) { toast.error('ระยะเวลาต้องอยู่ระหว่าง 0-60 เดือน'); return; }
    setSavingDuration(true);
    try {
      await axios.patch('/api/superadmin/settings/referral_duration_months',
        { value: String(n) },
        { headers: { Authorization: 'Bearer ' + superToken } }
      );
      toast.success('บันทึกระยะเวลา ' + n + ' เดือนแล้ว');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'บันทึกไม่สำเร็จ');
    } finally {
      setSavingDuration(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
        กำลังโหลด...
      </div>
    );
  }

  if (!data) return null;

  const fmt = (n) => Number(n || 0).toLocaleString('th-TH');

  return (
    <div style={{ padding: isMobile ? 8 : 20 }}>
      {/* Settings Card */}
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 12,
        padding: 20,
        marginBottom: 20
      }}>
        <h3 style={{ margin: 0, marginBottom: 16, color: '#0ea5e9' }}>⚙️ ตั้งค่าระบบแนะนำเพื่อน</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ color: '#e2e8f0', fontSize: 14 }}>ส่วนลด %:</label>
          <input
            type="number"
            min="0" max="100"
            value={newPercent}
            onChange={(e) => setNewPercent(e.target.value)}
            style={{
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: 6,
              padding: '8px 12px',
              color: '#e2e8f0',
              width: 80,
              fontSize: 14
            }}
          />
          <span style={{ color: '#94a3b8' }}>%</span>
          <button
            onClick={savePercent}
            disabled={savingPercent}
            style={{
              background: '#0ea5e9',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            {savingPercent ? 'กำลังบันทึก...' : '💾 บันทึก'}
          </button>
          <span style={{ color: '#64748b', fontSize: 13 }}>
            (ปัจจุบัน: <b style={{ color: '#0ea5e9' }}>{data.settings.percent}%</b>)
          </span>
          <div style={{ marginTop: 10, padding: 10, background: '#0f172a', borderRadius: 6, fontSize: 12, color: '#64748b', borderLeft: '3px solid #0ea5e9' }}>
            ℹ️ % นี้ใช้กับการแนะนำใหม่เท่านั้น — Credit ที่ได้รับไปแล้วจะคงเดิมตามสัญญาเดิม
          </div>
          {/* referral_duration_v1: global default duration */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid #334155' }}>
            <label style={{ color: '#e2e8f0', fontSize: 14 }}>⏳ ระยะเวลารับค่าคอม (ค่ากลาง):</label>
            <input type="number" min="0" max="60" value={newDuration}
              onChange={(e) => setNewDuration(e.target.value)}
              style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: '8px 12px', color: '#e2e8f0', width: 80, fontSize: 14 }} />
            <span style={{ color: '#94a3b8' }}>เดือน</span>
            <button onClick={saveDuration} disabled={savingDuration}
              style={{ background: '#0ea5e9', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 14 }}>
              {savingDuration ? 'กำลังบันทึก...' : '💾 บันทึก'}
            </button>
            <span style={{ color: '#64748b', fontSize: 13 }}>
              (นับจากการจ่ายครั้งแรกของลูกค้าที่ถูกแนะนำ — เกินกำหนดตัดค่าคอมอัตโนมัติ)
            </span>
          </div>
          <span style={{display:'none'}}>
          </span>
        </div>
      </div>

      {/* Stats summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 20
      }}>
        <StatBox icon="👥" label="Top Referrers" value={data.top_referrers.length} color="#22c55e" />
        <StatBox icon="📝" label="Recent" value={data.recent_referrals.length} color="#0ea5e9" />
        <StatBox icon="💰" label="Credit คงค้าง" value={fmt(data.total_credit_outstanding) + ' ฿'} color="#f59e0b" small />
        <StatBox icon="📊" label="Current %" value={data.settings.percent + '%'} color="#a855f7" />
      </div>

      {/* Top Referrers */}
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 12,
        padding: isMobile ? 12 : 20,
        marginBottom: 20
      }}>
        <h3 style={{ margin: 0, marginBottom: 12, color: '#22c55e' }}>🏆 Top Referrers</h3>
        {data.top_referrers.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 14, padding: 20, textAlign: 'center' }}>
            ยังไม่มีผู้แนะนำ
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: '#94a3b8', borderBottom: '1px solid #334155' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Tenant</th>
                  <th style={{ padding: 8, textAlign: 'left' }}>Code</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>ชวน</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>สำเร็จ</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Credit คงเหลือ</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>รวมรางวัล</th>
                  <th style={{ padding: 8, textAlign: 'center' }}>Action</th>{/* admin_credit_adjust_ui_v1 */}
                </tr>
              </thead>
              <tbody>
                {data.top_referrers.map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #1e293b' }}>
                    <td style={{ padding: 8, color: '#e2e8f0' }}>{t.name}</td>
                    <td style={{ padding: 8, color: '#0ea5e9', fontFamily: 'monospace' }}>{t.referral_code}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#94a3b8' }}>{t.total_referrals}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#22c55e' }}>{t.credited_count}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#f59e0b' }}>{fmt(t.referral_credit)}</td>
                    <td style={{ padding: 8, textAlign: 'right', color: '#a855f7' }}>{fmt(t.total_earned)} ฿</td>
                    {/* admin_credit_adjust_ui_v1: Action button */}
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      <button onClick={() => setCreditAdjustTenant(t)} title="ปรับ Credit"
                        style={{
                          padding: '4px 10px', borderRadius: 6,
                          background: '#f59e0b', color: '#fff',
                          border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600
                        }}>
                        💰 ปรับ
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* admin_credit_adjust_ui_v1: Credit Adjust Modal */}
      {creditAdjustTenant && (
        <CreditAdjustModal
          tenant={creditAdjustTenant}
          token={superToken}
          onClose={() => setCreditAdjustTenant(null)}
          onSaved={() => { setCreditAdjustTenant(null); load(); }}
        />
      )}

      {/* Recent Referrals */}
      <div style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 12,
        padding: isMobile ? 12 : 20
      }}>
        <h3 style={{ margin: 0, marginBottom: 12, color: '#0ea5e9' }}>📝 ประวัติการแนะนำล่าสุด</h3>
        {data.recent_referrals.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 14, padding: 20, textAlign: 'center' }}>
            ยังไม่มีประวัติ
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.recent_referrals.map(r => {
              const statusColor = r.status === 'credited' ? '#22c55e' : r.status === 'pending' ? '#f59e0b' : '#94a3b8';
              const statusLabel = r.status === 'credited' ? '✅ Credited' : r.status === 'pending' ? '⏳ Pending' : '❌ Reverted';
              return (
                <div key={r.id} style={{
                  background: '#0f172a',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  padding: 12,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: 8,
                  fontSize: 13
                }}>
                  <div>
                    <div style={{ color: '#e2e8f0' }}>
                      <b>{r.referrer_name}</b> <span style={{ color: '#64748b' }}>→</span> <b>{r.referred_name}</b>
                    </div>
                    <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>
                      {new Date(r.created_at).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  </div>
                  <div style={{ textAlign: isMobile ? 'left' : 'right' }}>
                    <div style={{ color: '#a855f7', fontWeight: 600 }}>
                      +{fmt(r.reward_amount)} ฿ ({r.reward_percent}%)
                    </div>
                    <div style={{ color: statusColor, fontSize: 11, marginTop: 2 }}>
                      {statusLabel}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ icon, label, value, color, small }) {
  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: 10,
      padding: 14
    }}>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{icon} {label}</div>
      <div style={{
        fontSize: small ? 16 : 22,
        fontWeight: 'bold',
        color: color
      }}>{value}</div>
    </div>
  );
}

function BankAccountsCard({ token: superToken, isMobile }) {
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null); // null | {} (new) | {...row} (edit)
  const [busy, setBusy] = React.useState(false);

  const emptyForm = {
    account_type: 'bank', // bank_accounts_crypto_v1
    bank_name: '',
    account_number: '',
    account_name: '',
    crypto_asset: '',
    crypto_network: '',
    promptpay_id: '',
    promptpay_type: '',
    is_active: true,
    sort_order: 0,
    note: '',
  };

  const isCryptoForm = !!editing && editing.account_type === 'crypto';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await SA.get('/api/superadmin/bank-accounts',
        { headers: { Authorization: `Bearer ${superToken}` } });
      setAccounts(data.bank_accounts || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'โหลดบัญชีไม่ได้');
    }
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const save = async () => {
    const f = editing;
    const safe = (v) => (v == null ? '' : String(v)).trim(); // null_safe_v1
    const isCrypto = f.account_type === 'crypto'; // bank_accounts_crypto_v1

    if (isCrypto) {
      if (!safe(f.crypto_asset)) { toast.error('กรุณากรอกชื่อเหรียญ'); return; }
      if (!safe(f.crypto_network)) { toast.error('กรุณาเลือกเครือข่าย (chain)'); return; }
      if (!safe(f.account_number)) { toast.error('กรุณากรอกที่อยู่กระเป๋า'); return; }
    } else {
      if (!safe(f.bank_name)) { toast.error('กรุณากรอกชื่อธนาคาร'); return; }
      if (!safe(f.account_number)) { toast.error('กรุณากรอกเลขบัญชี'); return; }
      if (!safe(f.account_name)) { toast.error('กรุณากรอกชื่อบัญชี'); return; }
      if (f.promptpay_type && !safe(f.promptpay_id)) { toast.error('กรุณากรอกเลข PromptPay'); return; }
      if (safe(f.promptpay_id) && !f.promptpay_type) { toast.error('กรุณาเลือกประเภท PromptPay'); return; }
    }

    setBusy(true);
    try {
      /* bank_accounts_crypto_v1: ส่งเฉพาะช่องที่เกี่ยวกับชนิดที่เลือก
         ชื่อที่โชว์ของคริปโต (bank_name) ให้ backend ประกอบเอง จะได้ไม่หลุดรูปแบบ */
      const payload = isCrypto ? {
        account_type: 'crypto',
        crypto_asset: safe(f.crypto_asset),
        crypto_network: safe(f.crypto_network),
        account_number: safe(f.account_number),
        account_name: safe(f.account_name),
        is_active: !!f.is_active,
        sort_order: parseInt(f.sort_order, 10) || 0,
        note: safe(f.note) || null,
      } : {
        account_type: 'bank',
        bank_name: safe(f.bank_name),
        account_number: safe(f.account_number),
        account_name: safe(f.account_name),
        promptpay_id: safe(f.promptpay_id) || null,
        promptpay_type: f.promptpay_type || null,
        is_active: !!f.is_active,
        sort_order: parseInt(f.sort_order, 10) || 0,
        note: safe(f.note) || null,
      };
      if (f.id) {
        await SA.patch(`/api/superadmin/bank-accounts/${f.id}`, payload,
          { headers: { Authorization: `Bearer ${superToken}` } });
        toast.success('✅ บันทึกแก้ไขสำเร็จ');
      } else {
        await SA.post('/api/superadmin/bank-accounts', payload,
          { headers: { Authorization: `Bearer ${superToken}` } });
        toast.success('✅ เพิ่มบัญชีสำเร็จ');
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'บันทึกไม่สำเร็จ');
    }
    setBusy(false);
  };

  const toggle = async (acc) => {
    try {
      await SA.patch(`/api/superadmin/bank-accounts/${acc.id}`,
        { is_active: !acc.is_active },
        { headers: { Authorization: `Bearer ${superToken}` } });
      toast.success(acc.is_active ? 'ปิดใช้งานแล้ว' : 'เปิดใช้งานแล้ว');
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'เปลี่ยนสถานะไม่ได้'); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`ลบบัญชี "${acc.bank_name} - ${acc.account_number}" ?`)) return;
    try {
      await SA.delete(`/api/superadmin/bank-accounts/${acc.id}`,
        { headers: { Authorization: `Bearer ${superToken}` } });
      toast.success('🗑️ ลบเรียบร้อย');
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'ลบไม่สำเร็จ'); }
  };

  const ppLabel = (t) => {
    if (t === 'phone') return 'เบอร์มือถือ';
    if (t === 'citizen_id') return 'เลขบัตรประชาชน';
    if (t === 'ewallet') return 'e-Wallet ID';
    return '';
  };

  const fmtPP = (id, type) => {
    if (!id) return '—';
    if (type === 'phone' && id.length === 10) return `${id.slice(0,3)}-${id.slice(3,6)}-${id.slice(6)}`;
    if (type === 'citizen_id' && id.length === 13) return `${id.slice(0,1)}-${id.slice(1,5)}-${id.slice(5,10)}-${id.slice(10,12)}-${id.slice(12)}`;
    return id;
  };

  return (
    <div style={{
      background: '#1e293b',
      borderRadius: 14,
      padding: isMobile ? 16 : 24,
      border: '1px solid #334155',
      marginBottom: isMobile ? 16 : 24,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ background: '#0ea5e922', padding: 10, borderRadius: 10 }}>
          <CreditCard size={20} color="#0ea5e9" />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#f1f5f9' }}>
            💳 บัญชีรับชำระเงิน
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>
            บัญชีที่แสดงให้ลูกค้าตอนต่ออายุ ({accounts.length} บัญชี)
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...emptyForm })}
          style={{
            background: '#0ea5e9', color: '#fff', border: 'none',
            padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
          }}>
          <Plus size={16} /> เพิ่มบัญชี
        </button>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>กำลังโหลด...</div>
      ) : accounts.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', color: '#64748b',
          border: '1px dashed #334155', borderRadius: 10,
        }}>
          ยังไม่มีบัญชี — กดปุ่ม "เพิ่มบัญชี" เพื่อเริ่มต้น
        </div>
      ) : isMobile ? (
        /* Mobile: card list */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {accounts.map(a => (
            <div key={a.id} style={{
              background: '#0f172a', borderRadius: 10, padding: 14,
              border: '1px solid #334155', opacity: a.is_active ? 1 : 0.5,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ minWidth: 0 }}>
                  {/* bank_accounts_crypto_v1 */}
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {a.account_type === 'crypto' && <span style={cryptoTag()}>🪙 {a.crypto_network}</span>}
                    {a.account_type === 'crypto' ? (a.crypto_asset || a.bank_name) : a.bank_name}
                  </div>
                  <div style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-all' }}>{a.account_number}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{a.account_name}</div>
                </div>
                <span style={{
                  background: a.is_active ? '#22c55e22' : '#64748b22',
                  color: a.is_active ? '#22c55e' : '#94a3b8',
                  padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                }}>{a.is_active ? 'เปิด' : 'ปิด'}</span>
              </div>
              {a.promptpay_id && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
                  PromptPay ({ppLabel(a.promptpay_type)}): <span style={{ fontFamily: 'monospace' }}>{fmtPP(a.promptpay_id, a.promptpay_type)}</span>
                </div>
              )}
              {a.note && <div style={{ fontSize: 12, color: '#64748b', fontStyle: 'italic', marginBottom: 8 }}>📝 {a.note}</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button onClick={() => toggle(a)} style={miniBtn('#64748b')}>
                  <Power size={12} /> {a.is_active ? 'ปิด' : 'เปิด'}
                </button>
                <button onClick={() => setEditing({ ...a, note: a.note || '', promptpay_id: a.promptpay_id || '', promptpay_type: a.promptpay_type || '', account_type: a.account_type || 'bank', crypto_asset: a.crypto_asset || '', crypto_network: a.crypto_network || '' })} style={miniBtn('#0ea5e9')}>
                  <Edit2 size={12} /> แก้ไข
                </button>
                <button onClick={() => remove(a)} style={miniBtn('#ef4444')}>
                  <Trash2 size={12} /> ลบ
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop: table */
        <div style={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', color: '#94a3b8', textAlign: 'left' }}>
                <th style={th()}>#</th>
                {/* bank_accounts_crypto_v1: หัวคอลัมน์เดิมเขียนว่า "ธนาคาร/เลขบัญชี" ซึ่งใช้กับคริปโตไม่ได้ */}
                <th style={th()}>ช่องทาง</th>
                <th style={th()}>เลขบัญชี / ที่อยู่กระเป๋า</th>
                <th style={th()}>ชื่อบัญชี</th>
                <th style={th()}>PromptPay</th>
                <th style={th()}>สถานะ</th>
                <th style={{ ...th(), textAlign: 'right' }}>จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a, i) => (
                <tr key={a.id} style={{
                  borderBottom: '1px solid #334155',
                  opacity: a.is_active ? 1 : 0.5,
                }}>
                  <td style={td()}>{a.sort_order || i + 1}</td>
                  {/* bank_accounts_crypto_v1 */}
                  <td style={{ ...td(), fontWeight: 600, color: '#f1f5f9' }}>
                    {a.account_type === 'crypto' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={cryptoTag()}>🪙 {a.crypto_network}</span>
                        {a.crypto_asset || a.bank_name}
                      </span>
                    ) : a.bank_name}
                  </td>
                  <td style={{ ...td(), fontFamily: 'monospace', fontSize: a.account_type === 'crypto' ? 11 : 13, wordBreak: 'break-all', maxWidth: 260 }}>{a.account_number}</td>
                  <td style={td()}>{a.account_name}</td>
                  <td style={{ ...td(), fontFamily: 'monospace', fontSize: 12 }}>
                    {a.promptpay_id ? (
                      <div>
                        <div>{fmtPP(a.promptpay_id, a.promptpay_type)}</div>
                        <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'inherit' }}>{ppLabel(a.promptpay_type)}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td style={td()}>
                    <span style={{
                      background: a.is_active ? '#22c55e22' : '#64748b22',
                      color: a.is_active ? '#22c55e' : '#94a3b8',
                      padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                    }}>{a.is_active ? 'เปิด' : 'ปิด'}</span>
                  </td>
                  <td style={{ ...td(), textAlign: 'right' }}>
                    <button onClick={() => toggle(a)} title={a.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'} style={iconBtn('#64748b')}>
                      <Power size={14} />
                    </button>
                    <button onClick={() => setEditing({ ...a, note: a.note || '', promptpay_id: a.promptpay_id || '', promptpay_type: a.promptpay_type || '', account_type: a.account_type || 'bank', crypto_asset: a.crypto_asset || '', crypto_network: a.crypto_network || '' })} title="แก้ไข" style={iconBtn('#0ea5e9')}>
                      <Edit2 size={14} />
                    </button>
                    <button onClick={() => remove(a)} title="ลบ" style={iconBtn('#ef4444')}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {editing && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: 16,
        }} onClick={() => !busy && setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: '#1e293b', borderRadius: 14, padding: isMobile ? 20 : 28,
            border: '1px solid #334155', width: '100%', maxWidth: 500,
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>
                {editing.id ? '✏️ แก้ไขบัญชี' : '➕ เพิ่มบัญชีใหม่'}
              </h3>
              <button onClick={() => !busy && setEditing(null)}
                style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            {/* bank_accounts_crypto_v1: เลือกชนิดก่อน แล้วค่อยโชว์ช่องที่เกี่ยวข้อง
                ไม่ยัดทุกช่องมารวมกัน เพราะช่องของธนาคารกับคริปโตไม่มีอะไรใช้ร่วมกันเลย */}
            <Field label="ชนิดบัญชี *">
              <div style={{ display: 'flex', gap: 8 }}>
                {[
                  { v: 'bank', label: '🏦 โอนผ่านธนาคาร' },
                  { v: 'crypto', label: '🪙 โอนเหรียญ (Crypto)' },
                ].map(o => (
                  <button key={o.v} type="button"
                    onClick={() => setEditing({ ...editing, account_type: o.v })}
                    style={{
                      flex: 1, padding: '9px 10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer',
                      background: isCryptoForm === (o.v === 'crypto') ? '#0ea5e9' : 'transparent',
                      color: isCryptoForm === (o.v === 'crypto') ? '#fff' : '#94a3b8',
                      border: '1px solid ' + (isCryptoForm === (o.v === 'crypto') ? '#0ea5e9' : '#334155'),
                    }}>{o.label}</button>
                ))}
              </div>
            </Field>

            {isCryptoForm ? (
              <>
                <Field label="เหรียญ *">
                  <input type="text" value={editing.crypto_asset || ''}
                    onChange={(e) => setEditing({ ...editing, crypto_asset: e.target.value })}
                    placeholder="เช่น USDT, USDC, BTC"
                    style={inp()} />
                </Field>

                <Field label="เครือข่าย / Chain *">
                  <select value={CRYPTO_NETWORKS.includes(editing.crypto_network) ? editing.crypto_network : (editing.crypto_network ? '__other__' : '')}
                    onChange={(e) => setEditing({ ...editing, crypto_network: e.target.value === '__other__' ? ' ' : e.target.value })}
                    style={{ ...inp(), marginBottom: 8 }}>
                    <option value="">— เลือกเครือข่าย —</option>
                    {CRYPTO_NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
                    <option value="__other__">อื่นๆ (พิมพ์เอง)</option>
                  </select>
                  {!CRYPTO_NETWORKS.includes(editing.crypto_network) && editing.crypto_network !== '' && editing.crypto_network != null && (
                    <input type="text" value={editing.crypto_network.trim() === '' ? '' : editing.crypto_network}
                      onChange={(e) => setEditing({ ...editing, crypto_network: e.target.value })}
                      placeholder="พิมพ์ชื่อเครือข่าย"
                      style={inp()} />
                  )}
                  {/* เตือนตรงนี้เพราะเป็นจุดที่พลาดแล้วเงินหายถาวร ไม่มีใครกู้คืนให้ */}
                  <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6, lineHeight: 1.5 }}>
                    ⚠️ เครือข่ายต้องตรงกับที่อยู่กระเป๋า ถ้าลูกค้าโอนผิดเครือข่าย เงินจะหายถาวร กู้คืนไม่ได้
                  </div>
                </Field>

                <Field label="ที่อยู่กระเป๋า (Wallet Address) *">
                  <input type="text" value={editing.account_number}
                    onChange={(e) => setEditing({ ...editing, account_number: e.target.value })}
                    placeholder="เช่น TXn9k2Lm... หรือ 0xdF326Ae8..."
                    spellCheck={false}
                    style={{ ...inp(), fontFamily: 'monospace', fontSize: 12 }} />
                </Field>

                <Field label="ชื่อกระเป๋า (ไม่บังคับ)">
                  <input type="text" value={editing.account_name}
                    onChange={(e) => setEditing({ ...editing, account_name: e.target.value })}
                    placeholder="เช่น กระเป๋าหลัก — ไม่ใส่ก็ได้"
                    style={inp()} />
                </Field>
              </>
            ) : (
              <>
                <Field label="ชื่อธนาคาร *">
                  <input type="text" value={editing.bank_name}
                    onChange={(e) => setEditing({ ...editing, bank_name: e.target.value })}
                    placeholder="เช่น กสิกรไทย, ไทยพาณิชย์"
                    style={inp()} />
                </Field>

                <Field label="เลขที่บัญชี *">
                  <input type="text" value={editing.account_number}
                    onChange={(e) => setEditing({ ...editing, account_number: e.target.value })}
                    placeholder="123-4-56789-0"
                    style={inp()} />
                </Field>

                <Field label="ชื่อบัญชี *">
                  <input type="text" value={editing.account_name}
                    onChange={(e) => setEditing({ ...editing, account_name: e.target.value })}
                    placeholder="ชื่อ-นามสกุล หรือ ชื่อบริษัท"
                    style={inp()} />
                </Field>

                <Field label="PromptPay (ไม่บังคับ)">
                  <select value={editing.promptpay_type}
                    onChange={(e) => setEditing({ ...editing, promptpay_type: e.target.value })}
                    style={{ ...inp(), marginBottom: 8 }}>
                    <option value="">— ไม่ใช้ PromptPay —</option>
                    <option value="phone">เบอร์มือถือ (10 หลัก)</option>
                    <option value="citizen_id">เลขบัตรประชาชน (13 หลัก)</option>
                    <option value="ewallet">e-Wallet ID (15 หลัก)</option>
                  </select>
                  {editing.promptpay_type && (
                    <input type="text" value={editing.promptpay_id}
                      onChange={(e) => setEditing({ ...editing, promptpay_id: e.target.value })}
                      placeholder={
                        editing.promptpay_type === 'phone' ? '0812345678' :
                        editing.promptpay_type === 'citizen_id' ? '1234567890123' :
                        '012345678901234'
                      }
                      style={inp()} />
                  )}
                </Field>
              </>
            )}

            <Field label="ลำดับการแสดง">
              <input type="number" value={editing.sort_order}
                onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                placeholder="0 = แสดงก่อน, มากขึ้น = แสดงทีหลัง"
                style={inp()} />
            </Field>

            <Field label="หมายเหตุ (ไม่บังคับ)">
              <textarea value={editing.note}
                onChange={(e) => setEditing({ ...editing, note: e.target.value })}
                placeholder="เช่น บัญชีหลัก, ใช้สำหรับลูกค้า VIP"
                rows={2}
                style={{ ...inp(), fontFamily: 'inherit', resize: 'vertical' }} />
            </Field>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <input type="checkbox" id="ba_active" checked={!!editing.is_active}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <label htmlFor="ba_active" style={{ fontSize: 13, color: '#cbd5e1', cursor: 'pointer' }}>
                เปิดใช้งานบัญชีนี้ (แสดงให้ลูกค้าเห็น)
              </label>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditing(null)} disabled={busy}
                style={{
                  background: 'transparent', color: '#94a3b8',
                  border: '1px solid #334155', padding: '9px 18px',
                  borderRadius: 8, fontSize: 13, cursor: 'pointer',
                }}>ยกเลิก</button>
              <button onClick={save} disabled={busy}
                style={{
                  background: busy ? '#475569' : '#0ea5e9', color: '#fff',
                  border: 'none', padding: '9px 18px', borderRadius: 8,
                  fontSize: 13, fontWeight: 600,
                  cursor: busy ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <Save size={14} /> {busy ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 6, fontWeight: 600 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

/* game_providers_v1: รายชื่อค่ายเกมกลาง — ทุกร้านเห็นเหมือนกัน
   ร้านยังเพิ่มค่ายของตัวเองได้อยู่ (เก็บแยก tenant_id) ตรงนี้คุมเฉพาะของกลาง
   ปิดใช้งานดีกว่าลบ: ลบแล้วค่ายหายจากตัวเลือกทุกร้านทันที ส่วนปิดคือหยุดให้เลือกใหม่เฉยๆ */
function GameProvidersPanel({ superToken, isMobile }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const hdr = React.useMemo(() => ({ headers: { Authorization: 'Bearer ' + superToken } }), [superToken]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await SA.get('/api/superadmin/game-providers', hdr);
      setRows(data.providers || []);
    } catch (e) {
      if (!(e.response && e.response.status === 401)) toast.error('โหลดรายชื่อค่ายไม่ได้');
    }
    setLoading(false);
  }, [hdr]);

  React.useEffect(() => { load(); }, [load]);

  const add = async () => {
    const v = name.trim();
    if (!v) { toast.error('กรุณากรอกชื่อค่าย'); return; }
    setBusy(true);
    try {
      await SA.post('/api/superadmin/game-providers', { name: v, sort_order: rows.length + 1 }, hdr);
      toast.success('เพิ่มค่ายแล้ว');
      setName(''); await load();
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'เพิ่มไม่สำเร็จ'); }
    setBusy(false);
  };

  const patch = async (row, body) => {
    try {
      await SA.patch('/api/superadmin/game-providers/' + row.id, body, hdr);
      await load();
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'บันทึกไม่สำเร็จ'); }
  };

  const remove = async (row) => {
    const used = Number(row.games_using) || 0;
    const warn = used > 0
      ? 'ค่าย "' + row.name + '" มีเกมใช้อยู่ ' + used + ' รายการ\n\nลบแล้วเกมเดิมยังอยู่และยังโชว์ชื่อค่ายนี้ แต่จะไม่มีให้เลือกใหม่อีก\nถ้าแค่อยากหยุดให้เลือก แนะนำ "ปิด" แทนการลบ\n\nยืนยันลบ?'
      : 'ลบค่าย "' + row.name + '" ?';
    if (!window.confirm(warn)) return;
    try {
      await SA.delete('/api/superadmin/game-providers/' + row.id, hdr);
      toast.success('ลบแล้ว'); await load();
    } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'ลบไม่สำเร็จ'); }
  };

  const rename = (row) => {
    const v = window.prompt('ชื่อค่ายใหม่', row.name);
    if (v == null || v.trim() === row.name) return;
    patch(row, { name: v.trim() });
  };

  return (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: isMobile ? 16 : 20, marginTop: 16, border: '1px solid #334155' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: '#f1f5f9' }}>🎰 ค่ายเกมกลาง</h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
        รายชื่อที่ทุกร้านเห็นเหมือนกันในหน้าจัดการเกม · แต่ละร้านยังเพิ่มค่ายของตัวเองได้ ซึ่งร้านอื่นจะไม่เห็น
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <input value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="ชื่อค่าย เช่น Joker, Habanero"
          maxLength={60}
          style={{ flex: '1 1 240px', padding: '9px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 13 }} />
        <button onClick={add} disabled={busy}
          style={{ background: busy ? '#475569' : '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
          + เพิ่มค่าย
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>กำลังโหลด...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#64748b', border: '1px dashed #334155', borderRadius: 10 }}>
          ยังไม่มีค่ายกลาง
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', color: '#94a3b8', textAlign: 'left' }}>
                <th style={th()}>ลำดับ</th>
                <th style={th()}>ชื่อค่าย</th>
                <th style={th()}>เกมที่ใช้อยู่</th>
                <th style={th()}>สถานะ</th>
                <th style={{ ...th(), textAlign: 'right' }}>จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #334155', opacity: r.is_active ? 1 : 0.5 }}>
                  <td style={td()}>
                    <input type="number" defaultValue={r.sort_order}
                      onBlur={e => { const n = parseInt(e.target.value, 10); if (!Number.isNaN(n) && n !== r.sort_order) patch(r, { sort_order: n }); }}
                      style={{ width: 60, padding: '4px 6px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#cbd5e1', fontSize: 12 }} />
                  </td>
                  <td style={{ ...td(), fontWeight: 600, color: '#f1f5f9' }}>{r.name}</td>
                  <td style={td()}>{Number(r.games_using) || 0}</td>
                  <td style={td()}>
                    <span style={{ background: r.is_active ? '#22c55e22' : '#64748b22', color: r.is_active ? '#22c55e' : '#94a3b8', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                      {r.is_active ? 'เปิด' : 'ปิด'}
                    </span>
                  </td>
                  <td style={{ ...td(), textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => patch(r, { is_active: !r.is_active })} title={r.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'} style={iconBtn('#64748b')}><Power size={14} /></button>
                    <button onClick={() => rename(r)} title="เปลี่ยนชื่อ" style={iconBtn('#0ea5e9')}><Edit2 size={14} /></button>
                    <button onClick={() => remove(r)} title="ลบ" style={iconBtn('#ef4444')}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function inp() {
  return {
    width: '100%', padding: '9px 12px',
    background: '#0f172a', border: '1px solid #334155',
    borderRadius: 8, color: '#f1f5f9', fontSize: 13,
    outline: 'none', boxSizing: 'border-box',
  };
}

function th() {
  return { padding: '10px 12px', fontWeight: 600, fontSize: 12 };
}

function td() {
  return { padding: '12px', color: '#cbd5e1' };
}

/* bank_accounts_crypto_v1: ป้ายบอกเครือข่าย ต้องเด่นพอให้เห็นก่อนคัดลอกที่อยู่กระเป๋า */
function cryptoTag() {
  return {
    background: '#f59e0b22', color: '#f59e0b',
    padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
    whiteSpace: 'nowrap',
  };
}

function miniBtn(color) {
  return {
    background: color + '22', color: color, border: 'none',
    padding: '6px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flex: 1, justifyContent: 'center',
  };
}

function iconBtn(color) {
  return {
    background: 'transparent', color: color, border: 'none',
    padding: 6, marginLeft: 4, cursor: 'pointer', borderRadius: 6,
    display: 'inline-flex', alignItems: 'center',
  };
}

/* superadmin_2fa_v1 */
function TwoFactorCard({ isMobile }) {
  const auth2 = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('superadmin_token') } });
  const [enabled, setEnabled] = useState(null);
  const [step, setStep] = useState('idle');
  const [qr, setQr] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { SA.get('/api/superadmin/2fa/status', auth2()).then(r => setEnabled(r.data.enabled)).catch(() => {}); }, []);
  const startSetup = async () => { setBusy(true); try { const r = await SA.post('/api/superadmin/2fa/setup', {}, auth2()); setQr(r.data.qr); setSecret(r.data.secret); setStep('setup'); setBackup(null); } catch (e) { toast.error('เริ่มไม่สำเร็จ'); } setBusy(false); };
  const doEnable = async () => { setBusy(true); try { const r = await SA.post('/api/superadmin/2fa/enable', { token: code }, auth2()); setBackup(r.data.backup_codes); setEnabled(true); setStep('idle'); setCode(''); toast.success('เปิด 2FA แล้ว'); } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'รหัสไม่ถูกต้อง'); } setBusy(false); };
  const doDisable = async () => { const t = window.prompt('ใส่รหัส 6 หลัก (หรือรหัสสำรอง) เพื่อปิด 2FA'); if (!t) return; setBusy(true); try { await SA.post('/api/superadmin/2fa/disable', { token: t }, auth2()); setEnabled(false); setBackup(null); toast.success('ปิด 2FA แล้ว'); } catch (e) { toast.error((e.response && e.response.data && e.response.data.error) || 'รหัสไม่ถูกต้อง'); } setBusy(false); };
  return (
    <div style={{ background: '#1e293b', borderRadius: 12, padding: 20, marginTop: 16 }}>
      <h3 style={{ margin: '0 0 4px', color: '#f1f5f9', fontSize: 16, fontWeight: 700 }}>🔐 ยืนยันตัวตน 2 ชั้น (2FA)</h3>
      <p style={{ margin: '0 0 14px', color: '#94a3b8', fontSize: 13 }}>เพิ่มความปลอดภัยบัญชี Super Admin ด้วยรหัส 6 หลักจากแอป (Google Authenticator / Authy)</p>
      {enabled === true && step === 'idle' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ color: '#22c55e', fontWeight: 700 }}>✓ เปิดใช้งานอยู่</span>
          <button onClick={doDisable} disabled={busy} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>ปิด 2FA</button>
        </div>
      )}
      {enabled === false && step === 'idle' && (
        <button onClick={startSetup} disabled={busy} style={{ background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, cursor: 'pointer' }}>{busy ? '...' : 'เปิด 2FA'}</button>
      )}
      {step === 'setup' && (
        <div>
          <p style={{ color: '#cbd5e1', fontSize: 13, margin: '0 0 10px' }}>1) เปิดแอป Authenticator → สแกน QR นี้</p>
          {qr && <img src={qr} alt="2FA QR" style={{ width: 180, height: 180, background: '#fff', borderRadius: 8, padding: 6 }} />}
          <p style={{ color: '#64748b', fontSize: 11, margin: '8px 0', wordBreak: 'break-all' }}>หรือกรอกรหัสมือ: {secret}</p>
          <p style={{ color: '#cbd5e1', fontSize: 13, margin: '10px 0 6px' }}>2) ใส่รหัส 6 หลักจากแอปเพื่อยืนยัน</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input value={code} onChange={e => setCode(e.target.value)} placeholder="รหัส 6 หลัก" style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9', fontSize: 15, width: 130 }} />
            <button onClick={doEnable} disabled={busy} style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, cursor: 'pointer' }}>ยืนยันเปิด</button>
            <button onClick={() => { setStep('idle'); setCode(''); }} style={{ background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: 8, padding: '9px 14px', cursor: 'pointer' }}>ยกเลิก</button>
          </div>
        </div>
      )}
      {backup && (
        <div style={{ marginTop: 14, background: '#0f172a', border: '1px solid #f59e0b', borderRadius: 8, padding: 14 }}>
          <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 8 }}>⚠️ รหัสสำรอง (เก็บให้ดี — โชว์ครั้งเดียว!)</div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>ใช้ตอนทำมือถือหาย · แต่ละรหัสใช้ได้ครั้งเดียว</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 6, fontFamily: 'monospace', color: '#f1f5f9', fontSize: 14 }}>
            {backup.map((c, i) => (<span key={i}>{c}</span>))}
          </div>
        </div>
      )}
    </div>
  );
}

function SystemSettingsCard({ token: superToken, isMobile }) {
  const [info, setInfo] = React.useState(null);
  const [tokenInput, setTokenInput] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showInput, setShowInput] = React.useState(false);

  const load = async () => {
    try {
      const { data } = await SA.get('/api/superadmin/system-settings/slip2go',
        { headers: { Authorization: `Bearer ${superToken}` } });
      setInfo(data);
    } catch { toast.error('โหลดข้อมูลไม่ได้'); }
  };

  React.useEffect(() => { load(); }, []);

  const save = async () => {
    if (!tokenInput.trim()) { toast.error('กรุณากรอก token'); return; }
    if (tokenInput.trim().length < 10) { toast.error('token สั้นเกินไป'); return; }
    setBusy(true);
    try {
      await SA.put('/api/superadmin/system-settings/slip2go',
        { token: tokenInput.trim() },
        { headers: { Authorization: `Bearer ${superToken}` } });
      toast.success('✅ บันทึก token สำเร็จ');
      setTokenInput(''); setShowInput(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setBusy(false);
  };

  const test = async () => {
    setBusy(true);
    try {
      const { data } = await SA.post('/api/superadmin/system-settings/slip2go/test', {},
        { headers: { Authorization: `Bearer ${superToken}` } });
      if (data.success) toast.success('✅ ' + (data.message || 'Token ใช้งานได้'));
      else toast.error('❌ ' + (data.message || 'Token มีปัญหา'));
    } catch (e) { toast.error(e.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('ลบ token? — Auto verify ของทุก tenant จะหยุดทำงานทันที')) return;
    setBusy(true);
    try {
      await SA.put('/api/superadmin/system-settings/slip2go',
        { token: '' },
        { headers: { Authorization: `Bearer ${superToken}` } });
      toast.success('🗑️ ลบ token เรียบร้อย');
      await load();
    } catch (e) { toast.error(e.response?.data?.error || 'เกิดข้อผิดพลาด'); }
    setBusy(false);
  };

  if (!info) return <div style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>กำลังโหลด...</div>;

  const hasToken = info.has_token;

  return (
    <div style={{
      background: '#1e293b',
      borderRadius: 14,
      padding: isMobile ? 16 : 24,
      border: '1px solid #334155',
      marginBottom: isMobile ? 16 : 24,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <div style={{
          background: hasToken ? '#22c55e22' : '#ef444422',
          padding: 10, borderRadius: 10,
        }}>
          <Key size={20} color={hasToken ? '#22c55e' : '#ef4444'} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#f1f5f9' }}>
            🔐 Slip2Go Token (Global)
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>
            ใช้ร่วมกันทุก tenant — แก้ที่นี่ที่เดียว
          </p>
        </div>
      </div>

      {/* Status */}
      <div style={{
        background: hasToken ? '#16a34a15' : '#dc262615',
        border: `1px solid ${hasToken ? '#22c55e44' : '#ef444444'}`,
        borderRadius: 10, padding: 14, marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hasToken ? 8 : 0 }}>
          {hasToken ? <CheckCircle size={18} color="#22c55e" /> : <AlertCircle size={18} color="#ef4444" />}
          <span style={{ fontSize: 14, fontWeight: 600, color: hasToken ? '#22c55e' : '#ef4444' }}>
            {hasToken ? 'Token พร้อมใช้งาน' : 'ยังไม่ได้ตั้งค่า Token'}
          </span>
        </div>
        {hasToken && (
          <div style={{ marginTop: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#94a3b8', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', background: '#0f172a', padding: '4px 8px', borderRadius: 4, color: '#cbd5e1' }}>
                {info.token_preview}
              </span>
              <span>• {info.token_length} ตัวอักษร</span>
              {info.updated_at && (
                <span>• อัปเดต: {new Date(info.updated_at).toLocaleString('th-TH')}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!showInput ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowInput(true)} disabled={busy}
            style={{
              padding: '10px 16px', borderRadius: 8, background: '#0ea5e9', color: '#fff',
              border: 'none', fontWeight: 600, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <Edit2 size={14} /> {hasToken ? 'เปลี่ยน Token' : 'ตั้งค่า Token'}
          </button>
          {hasToken && (
            <>
              <button onClick={test} disabled={busy}
                style={{
                  padding: '10px 16px', borderRadius: 8, background: '#22c55e', color: '#fff',
                  border: 'none', fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.6 : 1, fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <CheckCircle size={14} /> ทดสอบ
              </button>
              <button onClick={remove} disabled={busy}
                style={{
                  padding: '10px 16px', borderRadius: 8, background: '#ef444422', color: '#ef4444',
                  border: '1px solid #ef444444', fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
                  opacity: busy ? 0.6 : 1, fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <Trash2 size={14} /> ลบ
              </button>
            </>
          )}
        </div>
      ) : (
        <div>
          <label style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6, display: 'block' }}>
            🔑 Slip2Go Token (จาก app.slip2go.com → Authentication)
          </label>
          <input
            type="password"
            value={tokenInput}
            onChange={e => setTokenInput(e.target.value)}
            placeholder="กรุณาวาง token ที่นี่..."
            autoFocus
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: '1px solid #334155', background: '#0f172a', color: '#f1f5f9',
              fontSize: 13, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={save} disabled={busy || !tokenInput.trim()}
              style={{
                padding: '10px 16px', borderRadius: 8, background: '#22c55e', color: '#fff',
                border: 'none', fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
                opacity: (busy || !tokenInput.trim()) ? 0.6 : 1, fontSize: 13,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <Save size={14} /> บันทึก
            </button>
            <button onClick={() => { setShowInput(false); setTokenInput(''); }}
              style={{
                padding: '10px 16px', borderRadius: 8, background: 'transparent',
                color: '#94a3b8', border: '1px solid #334155',
                cursor: 'pointer', fontSize: 13,
              }}>
              ยกเลิก
            </button>
          </div>
          <div style={{ marginTop: 10, padding: 10, background: '#0c4a6e22', borderLeft: '3px solid #0ea5e9', borderRadius: 6, fontSize: 11, color: '#7dd3fc' }}>
            💡 Token จะถูกใช้กับทุก tenant ที่เปิด auto/manual verify
          </div>
        </div>
      )}
    </div>
  );
}

function TenantCard({ tenant, statusColor, planColor, onEdit, onToggle, onRenew, onQuota, isLast }) {
  const t = tenant;
  const isExpired = t.expires_at && new Date(t.expires_at) < new Date();
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #0f172a',
      opacity: t.is_active ? 1 : 0.7,
    }}>
      {/* Top: ชื่อ + plan + status */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: t.is_active ? '#f1f5f9' : '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.name}
            </div>
            <div style={{ fontSize: 12, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {t.email}
            </div>
          </div>
          {!t.is_active && (
            <span style={{ fontSize: 10, background: '#ef4444', color: '#fff', borderRadius: 4, padding: '2px 6px', flexShrink: 0, fontWeight: 700 }}>
              DISABLED
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span style={{
            background: planColor(t.plan) + '22',
            color: planColor(t.plan),
            borderRadius: 6, padding: '2px 9px',
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
          }}>
            {t.plan || '-'}
          </span>
          <span style={{
            background: statusColor(t.status) + '22',
            color: statusColor(t.status),
            borderRadius: 6, padding: '2px 9px',
            fontSize: 11, fontWeight: 600,
          }}>
            {t.status || '-'}
          </span>
          {t.expires_at && (
            <span style={{
              background: isExpired ? '#ef444422' : '#33415555',
              color: isExpired ? '#ef4444' : '#94a3b8',
              borderRadius: 6, padding: '2px 9px',
              fontSize: 11, fontWeight: 500,
            }}>
              💬 {new Date(t.expires_at).toLocaleDateString('th-TH')}
            </span>
          )}
          {/* combined_view_v1: บิล badge */}
          {t.has_bill_feature && (
            <span style={{
              background: t.bill_expires_at ? (new Date(t.bill_expires_at) < new Date() ? '#ef444422' : '#33415555') : '#f59e0b22',
              color: t.bill_expires_at ? (new Date(t.bill_expires_at) < new Date() ? '#ef4444' : '#94a3b8') : '#f59e0b',
              borderRadius: 6, padding: '2px 9px',
              fontSize: 11, fontWeight: 500,
            }}>
              🧾 {t.bill_expires_at ? new Date(t.bill_expires_at).toLocaleDateString('th-TH') : 'ยังไม่ตั้ง'}
            </span>
          )}
        </div>
      </div>

      {/* Usage stats — 3 cols */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, marginBottom: 8 }}>
        <UsageStat label="Social" current={t.channel_count} max={t.max_channels} icon="📡" />
        <UsageStat label="Admin" current={t.agent_count} max={t.max_agents} icon="👤" />
        <UsageStat label="KB" current={t.kb_count} max={t.max_kb} icon="🤖" />
      </div>

      {/* slip_quota_admin_v1: Slip Quota row */}
      {(() => {
        const used = t.slip_quota_used || 0;
        const total = (t.slip_quota_monthly || 0) + (t.slip_quota_topup || 0);
        const pct = total > 0 ? (used / total) * 100 : 0;
        const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';
        return (
          <div style={{ background: '#0f172a', padding: '8px 12px', borderRadius: 8, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: '#64748b' }}>💳 Slip Quota</span>
            <span style={{ fontSize: 12, fontWeight: 600, color }}>
              {used}/{total} {t.slip_quota_topup > 0 && <span style={{ color: '#22c55e' }}>(+{t.slip_quota_topup})</span>}
            </span>
          </div>
        );
      })()}

      {/* Note */}
      {t.note && (
        <div style={{
          fontSize: 12, color: '#64748b',
          background: '#0f172a', padding: '6px 10px',
          borderRadius: 6, marginBottom: 10,
          lineHeight: 1.4,
        }}>
          📝 {t.note}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onEdit(t)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#0ea5e920', color: '#0ea5e9',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, fontWeight: 500,
          }}>
          <Edit2 size={14} /> แก้ไข
        </button>
        {/* slip_quota_admin_v1: Quota button */}
        <button onClick={() => onQuota(t)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#22c55e20', color: '#22c55e',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, fontWeight: 500,
          }}>
          <CreditCard size={14} /> เครดิต
        </button>
        <button onClick={() => onToggle(t.id)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: t.is_active ? '#ef444420' : '#22c55e20',
            color: t.is_active ? '#ef4444' : '#22c55e',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, fontWeight: 500,
          }}>
          <Power size={14} /> {t.is_active ? 'ปิด' : 'เปิด'}
        </button>
        <button onClick={() => onRenew(t, 30)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#22c55e20', color: '#22c55e',
            border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 700,
          }}>
          +30 วัน
        </button>
      </div>
    </div>
  );
}

// mobile_v1: Pending Card สำหรับ mobile
function PendingCard({ tenant, onApprove, onReject, isLast }) {
  const t = tenant;
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #0f172a',
    }}>
      {/* Top */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', marginBottom: 2 }}>
          {t.name}
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6 }}>
          {t.email}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span style={{
            background: '#0ea5e920', color: '#0ea5e9',
            borderRadius: 6, padding: '2px 9px',
            fontSize: 11, fontWeight: 700,
          }}>
            📦 {t.package_name || 'ไม่ระบุ'}
          </span>
          <span style={{
            background: '#22c55e22', color: '#22c55e',
            borderRadius: 6, padding: '2px 9px',
            fontSize: 11, fontWeight: 700,
          }}>
            {t.price > 0 ? `฿${parseInt(t.price).toLocaleString()}` : 'ฟรี'}
          </span>
          <span style={{
            background: '#33415555', color: '#94a3b8',
            borderRadius: 6, padding: '2px 9px',
            fontSize: 11,
          }}>
            📅 {new Date(t.created_at).toLocaleDateString('th-TH')}
          </span>
        </div>
      </div>

      {/* Note */}
      {t.note && (
        <div style={{
          fontSize: 12, color: '#94a3b8',
          background: '#0f172a', padding: '6px 10px',
          borderRadius: 6, marginBottom: 10,
          lineHeight: 1.4,
        }}>
          📝 {t.note}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onApprove(t.id)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#22c55e20', color: '#22c55e',
            border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 700,
          }}>
          ✓ อนุมัติ
        </button>
        <button onClick={() => onReject(t.id)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#ef444420', color: '#ef4444',
            border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 700,
          }}>
          ✗ ปฏิเสธ
        </button>
      </div>
    </div>
  );
}

// mobile_v1: ตัวช่วย usage stat
function UsageStat({ label, current, max, icon }) {
  const cur = parseInt(current) || 0;
  const isMaxed = max && cur >= max;
  return (
    <div style={{
      background: '#0f172a',
      borderRadius: 8,
      padding: '8px 6px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2, fontWeight: 600 }}>{icon} {label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: isMaxed ? '#ef4444' : '#22c55e' }}>
        {cur}/{max || '∞'}
      </div>
    </div>
  );
}

// Style helpers
const Label = ({ children }) => (
  <label style={{
    fontSize: 12, fontWeight: 600, color: '#64748b',
    display: 'block', marginBottom: 4,
  }}>{children}</label>
);

const DarkLabel = ({ children }) => (
  <label style={{
    color: '#94a3b8', fontSize: 12,
    display: 'block', marginBottom: 4,
    fontWeight: 600,
  }}>{children}</label>
);

const inputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14,
  boxSizing: 'border-box',
  outline: 'none',
});

const darkInputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8,
  border: '1px solid #334155',
  background: '#0f172a',
  color: '#f1f5f9',
  fontSize: isMobile ? 16 : 14,
  boxSizing: 'border-box',
  outline: 'none',
});
